(() => {
  "use strict";

  const html = document.documentElement;
  const shopifyMode = Boolean(document.querySelector('script[data-pdp-runtime-mode="shopify"]'));
  const runtimeRoot = shopifyMode ? document.querySelector('main#MainContent') : document;
  if (!runtimeRoot) return;
  if (html.dataset.pdpInteractiveRuntime) return;
  html.dataset.pdpInteractiveRuntime = "loading";

  const SELECTED = "true";
  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");
  const overlays = "dialog,[role='dialog'],[data-pdp-dialog],[data-pdp-menu-panel],[data-pdp-search-panel],.gp-dialog,.gp-popup,[data-gp-popup]";
  const carouselTrackSelector = [
    "[data-pdp-carousel-track]",
    ".splide__list",
    ".swiper-wrapper",
    ".gem-slider-wrapper",
    ".slick-track",
    ".slideshow__slider",
    "slideshow-component > .slideshow",
    "slideshow-component > .slider"
  ].join(",");
  const carouselSlideSelector = [
    "[data-pdp-carousel-slide]",
    ".splide__slide",
    ".swiper-slide",
    ".gem-slider-item",
    ".slick-slide:not(.slick-cloned)",
    ".slideshow__slide"
  ].join(",");
  const carouselPreviousSelector = [
    "[data-pdp-carousel-prev]",
    ".splide__arrow--prev",
    ".swiper-button-prev",
    ".gem-slider-previous",
    ".slick-prev",
    ".slider-button--prev"
  ].join(",");
  const carouselNextSelector = [
    "[data-pdp-carousel-next]",
    ".splide__arrow--next",
    ".swiper-button-next",
    ".gem-slider-next",
    ".slick-next",
    ".slider-button--next"
  ].join(",");
  const carouselPageSelector = [
    "[data-pdp-carousel-page]",
    ".splide__pagination__page",
    ".swiper-pagination-bullet",
    ".gp-carousel-dot",
    ".slick-dots button",
    ".slider-counter__link"
  ].join(",");
  const dialogOpeners = new WeakMap();
  const dialogStateBaselines = new WeakMap();
  const bundleStyles = new WeakMap();
  const carouselStates = new WeakMap();
  const countdownStates = new WeakMap();
  const touchStarts = new WeakMap();
  const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
  const stickyObservers = [];
  let activeOverlay = null;
  let activeTouchOwner = null;
  let resizeFrame = 0;

  const all = (selector, root = runtimeRoot) => [...root.querySelectorAll(selector)];
  const one = (selector, root = runtimeRoot) => root.querySelector(selector);
  const closest = (target, selector) => target instanceof Element ? target.closest(selector) : null;
  const integer = (value, fallback = 0) => {
    const parsed = Number.parseInt(String(value ?? ""), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const setBoolean = (element, name, value) => {
    if (element) element.setAttribute(name, String(Boolean(value)));
  };
  const emitChange = (element) => element?.dispatchEvent(new Event("change", { bubbles: true }));
  const emitInputAndChange = (element) => {
    if (!element) return;
    element.dispatchEvent(new Event("input", { bubbles: true }));
    emitChange(element);
  };
  const inertAction = (target) => closest(target, [
    "button[name='add']",
    ".product-form__submit",
    ".main-product-atc",
    "[data-pdp-cart]",
    "[data-pdp-external-action]",
    "a[href*='/cart']",
    "a[href*='/checkout']",
    "a[href*='/account']"
  ].join(","));
  const unsafeAnchor = (anchor) => {
    if (!anchor) return false;
    const href = String(anchor.getAttribute("href") || "").trim();
    return Boolean(href && !href.startsWith("#"));
  };

  function selectorTarget(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;
    try {
      return document.querySelector(raw.startsWith("#") ? raw : `#${CSS.escape(raw)}`)
        || document.querySelector(raw);
    } catch {
      return null;
    }
  }

  function mark(element, attribute, value = "") {
    if (element && !element.hasAttribute(attribute)) element.setAttribute(attribute, value);
    return element;
  }

  function attributeIndex(element, attribute, fallback) {
    const value = element?.getAttribute(attribute);
    return /^\d+$/.test(String(value || "")) ? integer(value) : fallback;
  }

  function activeFocusables(root) {
    return all(focusableSelector, root).filter((element) => {
      const style = getComputedStyle(element);
      return style.visibility !== "hidden" && style.display !== "none" && element.getClientRects().length > 0;
    });
  }

  function setPageScrollLock(locked) {
    // `overflow: auto` on both html and body creates a nested scroll container
    // in Chromium. The page still scrolls, but native `position: sticky`
    // elements stop sticking to the viewport. Restore the normal document
    // scrolling model after a dialog closes instead.
    const value = locked ? "hidden" : "visible";
    const horizontalValue = locked ? "hidden" : "clip";
    html.dataset.pdpScrollLocked = String(locked);
    html.style.setProperty("overflow-x", horizontalValue, "important");
    html.style.setProperty("overflow-y", value, "important");
    if (document.body) {
      document.body.classList.toggle("pdp-interactive-scroll-lock", locked);
      document.body.style.setProperty("overflow-x", horizontalValue, "important");
      document.body.style.setProperty("overflow-y", value, "important");
    }
  }

  function revealCapturedScrollContent() {
    all(".animate-section.animate--hidden").forEach((root) => {
      if (!root.closest(overlays)) root.classList.replace("animate--hidden", "animate--shown");
    });
    all(".scroll-trigger.scroll-trigger--offscreen").forEach((root) => {
      if (!root.closest(overlays)) {
        root.classList.replace("scroll-trigger--offscreen", "animate--active");
      }
    });
    all("[data-aos]:not(.aos-animate)").forEach((root) => {
      if (!root.closest(overlays)) root.classList.add("aos-animate");
    });
    // GemPages leaves fully-rendered sections behind this lazy marker until its
    // remote observer runs. The offline capture intentionally removes that
    // script, so release only ordinary document content; modal/popup content
    // keeps its authored hidden state.
    all(".gps-lazy").forEach((root) => {
      if (root.closest(overlays)) return;
      root.classList.remove("gps-lazy");
      root.dataset.pdpCapturedLazy = "revealed";
    });
  }

  function releaseTransientHydrationStyles() {
    all("link[data-pdp-transient-hydration-style]").forEach((link) => {
      link.disabled = true;
      link.dataset.pdpTransientHydrationReleased = "true";
    });
  }

  function repairCapturedOverflowLists() {
    all("overflow-list").forEach((root) => {
      if (root.shadowRoot) return;
      root.style.display = "flex";
      root.style.alignItems = "stretch";
      root.style.whiteSpace = "nowrap";
      const overflowCount = Math.max(0, integer(getComputedStyle(root).getPropertyValue("--overflow-count")));
      all(":scope > [slot='overflow']", root).forEach((item) => {
        item.hidden = true;
        item.style.display = "none";
      });
      const more = one(":scope > [slot='more']", root);
      if (more) {
        more.hidden = overflowCount === 0;
        more.style.display = overflowCount === 0 ? "none" : "";
      }
    });
  }

  function gemPagesData(root) {
    try { return JSON.parse(root.getAttribute("gp-data") || "{}"); } catch { return {}; }
  }

  function configureCapturedMarquee(root) {
    const container = one(".rfm-marquee-container", root);
    let runs = container ? all(":scope > .rfm-marquee", container) : [];
    if (!container || !runs.length) return;
    if (runs.length === 1) {
      const clone = runs[0].cloneNode(true);
      clone.dataset.pdpGeneratedMarqueeRun = "true";
      clone.setAttribute("aria-hidden", "true");
      all("[id]", clone).forEach((element) => element.removeAttribute("id"));
      container.append(clone);
      runs = [runs[0], clone];
    }
    const data = gemPagesData(root);
    const setting = data.setting || {};
    const styles = data.styles || {};
    const mobile = matchMedia("(max-width: 767px)").matches;
    const items = all(".gp-marque-child-item > *", runs[0]);
    if (!items.length) return;
    const maximum = Number.parseFloat(styles.itemMaxWidth?.[mobile ? "mobile" : "desktop"]);
    const spacing = Number.parseFloat(styles.itemSpacing?.[mobile ? "mobile" : "desktop"]);
    const viewportWidth = container.getBoundingClientRect().width || root.getBoundingClientRect().width || innerWidth;
    const itemWidth = mobile
      ? (Number.isFinite(maximum) ? maximum : 150)
      : Math.min(Number.isFinite(maximum) ? maximum : 300, viewportWidth / items.length);
    const setWidth = Math.max(1, itemWidth * items.length);
    const configuredSpeed = Number.parseFloat(setting.speed);
    const pixelsPerSecond = Number.isFinite(configuredSpeed) && configuredSpeed > 0
      ? (configuredSpeed <= 5 ? configuredSpeed * 100 : configuredSpeed)
      : 50;
    root.style.setProperty("--pdp-marquee-item-width", `${itemWidth}px`);
    root.style.setProperty("--pdp-marquee-item-spacing", `${Number.isFinite(spacing) ? spacing : (mobile ? 20 : 60)}px`);
    root.style.setProperty("--pdp-marquee-set-width", `${setWidth}px`);
    root.style.setProperty("--pdp-marquee-duration", `${Math.max(12, Math.min(120, setWidth / pixelsPerSecond))}s`);
    root.dataset.pdpMarqueeDirection = setting.direction === "right" ? "right" : "left";
    root.dataset.pdpMarqueePauseHover = String(Boolean(setting.stopOnHover));
  }

  function annotateCapturedMarquees() {
    all("gp-marquee").forEach((root) => {
      mark(root, "data-pdp-marquee");
      configureCapturedMarquee(root);
    });
  }

  function setImageComparison(root, requestedPosition) {
    const data = gemPagesData(root);
    const vertical = Boolean(data.vertical);
    const position = Math.max(0, Math.min(100, Number(requestedPosition) || 0));
    const container = one("[data-testid='container']", root);
    const left = one("[data-testid='left-image']", root);
    const right = one("[data-testid='right-image']", root);
    const slider = one("[id^='image-comparison-slider-'], [data-pdp-image-comparison-slider]", root);
    if (!container || !left || !right || !slider) return;
    container.style.aspectRatio = String(data.aspectRatio?.desktop || data.defaultRatio || "1").replace("/", " / ");
    container.style.overflow = "hidden";
    [left, right].forEach((image) => {
      image.style.objectFit = "cover";
      image.style.inset = "0";
    });
    if (vertical) {
      left.style.clipPath = `inset(0 0 ${100 - position}% 0)`;
      slider.style.inset = `calc(${position}% - ${(Number(data.handleSize) || 40) / 2}px) 0 auto`;
      slider.style.width = "100%";
      slider.style.height = `${Number(data.handleSize) || 40}px`;
      slider.style.cursor = "ns-resize";
    } else {
      left.style.clipPath = `inset(0 ${100 - position}% 0 0)`;
      slider.style.inset = `0 auto 0 calc(${position}% - ${(Number(data.handleSize) || 40) / 2}px)`;
      slider.style.width = `${Number(data.handleSize) || 40}px`;
      slider.style.height = "100%";
      slider.style.cursor = "ew-resize";
    }
    root.dataset.pdpPosition = String(position);
    slider.setAttribute("aria-valuenow", String(Math.round(position)));
  }

  function annotateImageComparisons() {
    all("gp-image-comparison").forEach((root) => {
      mark(root, "data-pdp-image-comparison");
      const data = gemPagesData(root);
      const container = one("[data-testid='container']", root);
      const slider = one("[id^='image-comparison-slider-']", root);
      if (!container || !slider) return;
      all("[data-testid='temp-image']", root).forEach((image) => {
        image.hidden = true;
        image.style.display = "none";
      });
      mark(slider, "data-pdp-image-comparison-slider");
      slider.tabIndex = 0;
      slider.setAttribute("role", "slider");
      slider.setAttribute("aria-label", "Image comparison position");
      slider.setAttribute("aria-valuemin", "0");
      slider.setAttribute("aria-valuemax", "100");
      slider.setAttribute("aria-orientation", data.vertical ? "vertical" : "horizontal");
      const updateFromPointer = (event) => {
        const rect = container.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const position = data.vertical
          ? ((event.clientY - rect.top) / rect.height) * 100
          : ((event.clientX - rect.left) / rect.width) * 100;
        setImageComparison(root, position);
      };
      container.addEventListener("pointerdown", (event) => {
        root.dataset.pdpDragging = "true";
        container.setPointerCapture?.(event.pointerId);
        updateFromPointer(event);
      });
      container.addEventListener("pointermove", (event) => {
        if (root.dataset.pdpDragging === SELECTED || data.hover) updateFromPointer(event);
      });
      const stopDragging = (event) => {
        root.dataset.pdpDragging = "false";
        try { container.releasePointerCapture?.(event.pointerId); } catch {}
      };
      container.addEventListener("pointerup", stopDragging);
      container.addEventListener("pointercancel", stopDragging);
      slider.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
        event.preventDefault();
        const current = Number(root.dataset.pdpPosition) || 0;
        if (event.key === "Home") setImageComparison(root, 0);
        else if (event.key === "End") setImageComparison(root, 100);
        else setImageComparison(root, current + (["ArrowRight", "ArrowDown"].includes(event.key) ? 5 : -5));
      });
      setImageComparison(root, (Number(data.sliderPositionPercentage) || 0.5) * 100);
    });
  }

  function lockPage(owner) {
    activeOverlay = owner;
    setPageScrollLock(true);
  }

  function unlockPage(owner) {
    if (activeOverlay && activeOverlay !== owner) return;
    activeOverlay = null;
    setPageScrollLock(false);
  }

  function annotateGallery(root) {
    mark(root, "data-pdp-gallery");
    mark(one("[data-pdp-gallery-track], .product__media-list", root), "data-pdp-gallery-track");
    const slides = all("[data-pdp-gallery-slide], .product__media-list > li", root);
    const controls = all("[data-pdp-gallery-thumb], .thumbnail", root);
    const thumbnailCount = String(Math.max(1, Math.min(5, controls.length || slides.length)));
    const galleryStyle = getComputedStyle(root);
    if (!galleryStyle.getPropertyValue("--desktop-thumbnails").trim()) {
      root.style.setProperty("--desktop-thumbnails", thumbnailCount);
    }
    if (!galleryStyle.getPropertyValue("--mobile-thumbnails").trim()) {
      root.style.setProperty("--mobile-thumbnails", thumbnailCount);
    }
    slides.forEach((slide, index) => mark(slide, "data-pdp-gallery-slide", String(index)));
    controls.forEach((control, index) => mark(control, "data-pdp-gallery-thumb", String(index)));
    const galleryTrack = one("[data-pdp-gallery-track]", root);
    const galleryViewport = galleryTrack?.closest("slider-component") || root;
    const galleryControlSelector = galleryViewport === root
      ? ":scope > [data-pdp-gallery-prev], :scope > .slider-button--prev"
      : "[data-pdp-gallery-prev], .slider-button--prev";
    const galleryNextSelector = galleryViewport === root
      ? ":scope > [data-pdp-gallery-next], :scope > .slider-button--next"
      : "[data-pdp-gallery-next], .slider-button--next";
    const previous = one(galleryControlSelector, galleryViewport);
    const next = one(galleryNextSelector, galleryViewport);
    mark(previous, "data-pdp-gallery-prev");
    mark(next, "data-pdp-gallery-next");
    const initial = Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active") || slide.getAttribute("aria-hidden") === "false"));
    showGallery(root, initial < 0 ? 0 : initial, false);
  }

  function galleryIndex(root, control) {
    const explicit = control.getAttribute("data-pdp-gallery-thumb");
    if (/^\d+$/.test(String(explicit || ""))) return integer(explicit);
    const slides = all("[data-pdp-gallery-slide]", root);
    const targetId = control.getAttribute("aria-controls") || control.dataset.target || control.dataset.mediaId;
    if (targetId) {
      const normalized = String(targetId).replace(/^#/, "");
      const match = slides.findIndex((slide) => slide.id === normalized
        || slide.dataset.mediaId === normalized
        || String(slide.dataset.mediaId || "").endsWith(normalized));
      if (match >= 0) return match;
    }
    return all("[data-pdp-gallery-thumb]", root).indexOf(control);
  }

  function scrollTrackToChild(track, child) {
    if (!track || !child) return;
    const left = Math.max(0, child.offsetLeft - track.offsetLeft);
    if (typeof track.scrollTo === "function") track.scrollTo({ left, behavior: "auto" });
    else track.scrollLeft = left;
  }

  function showGallery(root, requestedIndex, focusControl = false, moveTrack = true) {
    const slides = all("[data-pdp-gallery-slide]", root);
    if (!slides.length) return;
    const controls = all("[data-pdp-gallery-thumb]", root);
    const index = (requestedIndex + slides.length) % slides.length;
    root.dataset.pdpIndex = String(index);
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      slide.dataset.pdpActive = String(active);
      slide.classList.toggle("is-active", active);
      if (slide.dataset.pdpGalleryActiveClass) slide.classList.toggle(slide.dataset.pdpGalleryActiveClass, active);
      setBoolean(slide, "aria-hidden", !active);
    });
    controls.forEach((control, controlIndex) => {
      const active = galleryIndex(root, control) === index || controlIndex === index;
      control.dataset.pdpActive = String(active);
      if (control.dataset.pdpGalleryActiveClass) control.classList.toggle(control.dataset.pdpGalleryActiveClass, active);
      if (active) control.setAttribute("aria-current", "true");
      else control.removeAttribute("aria-current");
      if (focusControl && active) control.focus({ preventScroll: true });
      const strip = control.closest('[data-pdp-scroll-strip-track]');
      if (active && strip && root.dataset.pdpRevealSelectedThumbnail === SELECTED) {
        const rect = control.getBoundingClientRect();
        const viewport = strip.getBoundingClientRect();
        if (rect.left < viewport.left || rect.right > viewport.right) scrollTrackToChild(strip, control);
      }
    });
    if (moveTrack) {
      const track = one("[data-pdp-gallery-track], .product__media-list", root);
      scrollTrackToChild(track, slides[index]);
    }
    const status = one("[data-pdp-gallery-status], [role='status']", root);
    if (status) status.textContent = `Media ${index + 1} of ${slides.length}`;
  }

  function annotateScrollStrips() {
    all("[data-pdp-scroll-strip], slider-component").forEach((root) => {
      const track = one("[data-pdp-scroll-strip-track], :scope > .slider, :scope > ul.slider", root);
      const previous = one("[data-pdp-scroll-strip-prev], .slider-button--prev", root);
      const next = one("[data-pdp-scroll-strip-next], .slider-button--next", root);
      if (!track || track.hasAttribute("data-pdp-gallery-track") || (!previous && !next)) return;
      mark(root, "data-pdp-scroll-strip");
      mark(track, "data-pdp-scroll-strip-track");
      mark(previous, "data-pdp-scroll-strip-prev");
      mark(next, "data-pdp-scroll-strip-next");
      syncScrollStrip(root);
    });
  }

  function scrollStripStep(root, direction, control) {
    const track = one("[data-pdp-scroll-strip-track]", root);
    if (!track) return;
    const items = [...track.children].filter((item) => item.getClientRects().length > 0);
    const itemDistance = items.length > 1
      ? Math.abs(items[1].offsetLeft - items[0].offsetLeft)
      : items[0]?.getBoundingClientRect().width || track.clientWidth;
    const requestedStep = Math.max(1, integer(control?.dataset.step, 1));
    const distance = Math.max(1, itemDistance * requestedStep || track.clientWidth * 0.8);
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const nextLeft = Math.max(0, Math.min(maximum, track.scrollLeft + direction * distance));
    const previousScrollBehavior = track.style.scrollBehavior;
    track.style.scrollBehavior = "auto";
    if (typeof track.scrollTo === "function") track.scrollTo({ left: nextLeft, behavior: "auto" });
    else track.scrollLeft = nextLeft;
    track.style.scrollBehavior = previousScrollBehavior;
    syncScrollStrip(root);
    requestAnimationFrame(() => syncScrollStrip(root));
  }

  function syncScrollStrip(root) {
    const track = one("[data-pdp-scroll-strip-track]", root);
    if (!track) return;
    const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
    const position = Math.max(0, Math.min(maximum, track.scrollLeft));
    root.dataset.pdpScrollPosition = String(Math.round(position));
    const previous = one("[data-pdp-scroll-strip-prev]", root);
    const next = one("[data-pdp-scroll-strip-next]", root);
    if (previous) previous.disabled = position <= 1;
    if (next) next.disabled = maximum <= 1 || position >= maximum - 1;
  }

  function disclosureParts(root) {
    const candidateDetails = root.matches("details") ? root : one("details", root);
    const trigger = one("[data-pdp-disclosure-trigger], .faq-item__header, .faq-question, .store-faq-question, .c-guide-trigger, .gp-accordion-item_header", root) || candidateDetails?.querySelector("summary") || one("[aria-controls]", root);
    // A custom disclosure can contain an independent native tooltip. Only
    // toggle a details element when its summary owns this disclosure trigger.
    const details = candidateDetails?.querySelector(":scope > summary")?.contains(trigger) ? candidateDetails : null;
    const controlled = selectorTarget(trigger?.getAttribute("aria-controls"));
    const panel = one("[data-pdp-disclosure-panel], .faq-item__answer, .faq-answer, .store-faq-answer, .c-guide-popup, .gp-accordion-item_body", root)
      || (controlled && (root.contains(controlled) || root.parentElement?.contains(controlled)) ? controlled : null)
      || (details ? [...details.children].find((child) => child.tagName !== "SUMMARY") : null);
    return { details, trigger, panel };
  }

  function annotateDisclosure(root) {
    mark(root, "data-pdp-disclosure");
    const { details, trigger, panel } = disclosureParts(root);
    mark(trigger, "data-pdp-disclosure-trigger");
    mark(panel, "data-pdp-disclosure-panel");
    setDisclosure(root, Boolean(details?.open
      || trigger?.getAttribute("aria-expanded") === SELECTED
      || root.dataset.pdpActive === SELECTED
      || root.classList.contains("active")
      || root.classList.contains("is-open")), false);
  }

  function disclosureAccordionGroup(root) {
    const explicit = root.closest("[data-pdp-disclosure-group]");
    if (explicit) {
      const mode = String(explicit.dataset.pdpDisclosureMode || explicit.dataset.pdpAccordion || "").toLowerCase();
      const exclusive = String(explicit.dataset.pdpExclusive || "").toLowerCase();
      if (["multiple", "false", "off", "none"].includes(mode) || exclusive === "false") return null;
      if (["single", "true", "on", "accordion"].includes(mode) || exclusive === "true") return explicit;
    }
    if (root.matches(".gp-accordion-item")) return root.closest("gp-accordion");
    if (!root.matches(".faq-item")) return null;
    return root.closest(".faq-section, .faq-list, .faqs, [data-faq-accordion]");
  }

  function closeDisclosureSiblings(root) {
    const group = disclosureAccordionGroup(root);
    if (!group) return;
    all("[data-pdp-disclosure]", group).forEach((sibling) => {
      if (sibling === root || disclosureAccordionGroup(sibling) !== group) return;
      setDisclosure(sibling, false, false);
    });
  }

  function setDisclosure(root, open, focus = false) {
    if (open) closeDisclosureSiblings(root);
    const { details, trigger, panel } = disclosureParts(root);
    if (details) details.open = open;
    const cls=root.dataset.pdpDisclosureOpenClass;
    if (cls && /^[\w-]+$/.test(cls)) root.classList.toggle(cls, open);
    if (root.dataset.pdpDisclosureExpandHeight === "true" && panel) panel.style.maxHeight = open ? "none" : "0";
    const icon=trigger && one("[data-pdp-disclosure-indicator]", trigger);
    if (icon) icon.textContent=open ? "−" : "+";
    if (root.matches(".faq-item, .store-faq-item")) {
      root.classList.toggle("active", open);
      // Captures can retain section-ID rules that cap even an active answer.
      // The interaction owns this height; long answers must not be clipped.
      if (panel) { panel.style.setProperty("max-height", open ? "none" : "0px", "important"); panel.style.setProperty("opacity", open ? "1" : "0", "important"); }
    }
    if (root.matches(".c-guide")) root.classList.toggle("is-open", open);
    if (root.matches(".gp-accordion-item") && panel) {
      panel.style.gridTemplateRows = open ? "1fr" : "0fr";
      panel.style.maxHeight = open ? "none" : "0px";
      panel.classList.toggle("gp-grid-rows-[0fr]", !open);
      panel.classList.toggle("gp-grid-rows-[1fr]", open);
      one(".gp-accordion-item_icon", root)?.classList.toggle("is-open", open);
    }
    root.dataset.pdpActive = String(open);
    setBoolean(trigger, "aria-expanded", open);
    setBoolean(panel, "aria-hidden", !open);
    if (panel && panel !== details) { panel.hidden = !open; panel.inert = !open; }
    if (focus) trigger?.focus({ preventScroll: true });
  }

  const scrollTopOriginalBottom = new WeakMap();
  function repairScrollTopPlacement() {
    all('[data-pdp-utility-action="scroll-top"]').forEach((action) => {
      if (!scrollTopOriginalBottom.has(action)) scrollTopOriginalBottom.set(action, action.style.bottom);
      action.style.bottom = scrollTopOriginalBottom.get(action);
      delete action.dataset.pdpScrollTopPlacement;
      if (getComputedStyle(action).position !== "fixed") return;
      const box = action.getBoundingClientRect();
      const hit = document.elementFromPoint(box.left + box.width / 2, box.top + box.height / 2);
      if (!hit || action.contains(hit)) return;
      const blocker = hit.closest("a[href]");
      if (!blocker || getComputedStyle(blocker).position !== "fixed") return;
      const obstacle = blocker.getBoundingClientRect();
      if (obstacle.width < innerWidth / 2 || obstacle.top < innerHeight / 2) return;
      action.style.bottom = `${innerHeight - obstacle.top + 12}px`;
      action.dataset.pdpScrollTopPlacement = "above-sticky-cta";
    });
  }

  function activateUtility(action) {
    const mode = String(action?.getAttribute("data-pdp-utility-action") || "").trim().toLowerCase();
    if (mode === "scroll-top") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return true;
    }
    const target = selectorTarget(action?.getAttribute("data-pdp-utility-target"));
    if (mode !== "reveal-disclosure" || !target?.matches("[data-pdp-disclosure]")) return false;
    setDisclosure(target, true, false);
    target.scrollIntoView({ behavior: "auto", block: "start" });
    return true;
  }

  function isNativeDialog(dialog) {
    return typeof HTMLDialogElement !== "undefined" && dialog instanceof HTMLDialogElement;
  }

  function annotateDialogs() {
    all("[data-pdp-dialog], modal-dialog.product-popup-modal").forEach((dialog) => {
      mark(dialog, "data-pdp-dialog");
      if (isNativeDialog(dialog) && dialog.open) {
        try {
          dialog.close();
        } catch {
          dialog.removeAttribute("open");
        }
      }
      dialog.dataset.pdpActive = "false";
      dialog.setAttribute("aria-hidden", "true");
      all("[data-pdp-dialog-close], .product-popup-modal__toggle", dialog)
        .forEach((close) => mark(close, "data-pdp-dialog-close"));
    });
    all("[data-pdp-dialog-open], modal-opener[data-modal]").forEach((opener) => {
      const button = opener.matches("button, [role='button']")
        ? opener
        : one("button, [role='button']", opener);
      if (!button) return;
      const target = button.getAttribute("data-pdp-dialog-open")
        || opener.getAttribute("data-pdp-dialog-open")
        || opener.getAttribute("data-modal")
        || button.getAttribute("aria-controls");
      mark(button, "data-pdp-dialog-open", target || "");
      button.setAttribute("aria-haspopup", "dialog");
      setBoolean(button, "aria-expanded", false);
    });
  }

  function dialogFor(opener) {
    const value = opener.getAttribute("data-pdp-dialog-open")
      || opener.closest("modal-opener")?.getAttribute("data-modal")
      || opener.getAttribute("aria-controls");
    return selectorTarget(value) || one("[data-pdp-dialog]");
  }

  function dialogStateKey(control) {
    return String(control?.getAttribute("data-pdp-dialog-state-select")
      || control?.getAttribute("data-pdp-dialog-state")
      || "").trim();
  }

  function dialogStateItems(dialog) {
    const explicit = all("[data-pdp-dialog-state-item]", dialog);
    if (explicit.length) return explicit;
    return all("[data-pdp-dialog-state]", dialog).filter((element) => (
      !element.matches("[data-pdp-dialog-open],[data-pdp-dialog-state-select],button,a,input,select,textarea")
    ));
  }

  function captureDialogStateBaseline(dialog) {
    if (!dialog || dialogStateBaselines.has(dialog)) return;
    const nodes = uniqueDialogStateNodes(dialog);
    dialogStateBaselines.set(dialog, {
      activeState: dialog.getAttribute("data-pdp-dialog-active-state"),
      nodes: nodes.map((element) => ({
        element,
        hidden: element.hasAttribute("hidden"),
        ariaHidden: element.getAttribute("aria-hidden"),
        ariaCurrent: element.getAttribute("aria-current"),
        ariaPressed: element.getAttribute("aria-pressed"),
        selected: element.getAttribute("data-pdp-selected"),
        active: element.getAttribute("data-pdp-active")
      }))
    });
  }

  function uniqueDialogStateNodes(dialog) {
    return [...new Set([
      ...dialogStateItems(dialog),
      ...all("[data-pdp-dialog-state-select]", dialog)
    ])];
  }

  function restoreDialogStateBaseline(dialog) {
    const baseline = dialogStateBaselines.get(dialog);
    if (!baseline) return;
    const restoreAttribute = (element, name, value) => {
      if (value == null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    };
    restoreAttribute(dialog, "data-pdp-dialog-active-state", baseline.activeState);
    baseline.nodes.forEach((state) => {
      if (!state.element.isConnected) return;
      state.element.toggleAttribute("hidden", state.hidden);
      restoreAttribute(state.element, "aria-hidden", state.ariaHidden);
      restoreAttribute(state.element, "aria-current", state.ariaCurrent);
      restoreAttribute(state.element, "aria-pressed", state.ariaPressed);
      restoreAttribute(state.element, "data-pdp-selected", state.selected);
      restoreAttribute(state.element, "data-pdp-active", state.active);
    });
    dialogStateBaselines.delete(dialog);
  }

  function applyDialogState(dialog, key, { captureBaseline = false } = {}) {
    const stateKey = String(key || "").trim();
    if (!dialog || !stateKey) return false;
    if (captureBaseline) captureDialogStateBaseline(dialog);
    const items = dialogStateItems(dialog);
    const matchingItems = items.filter((item) => String(
      item.getAttribute("data-pdp-dialog-state-item") || item.getAttribute("data-pdp-dialog-state") || ""
    ).trim() === stateKey);
    if (items.length && !matchingItems.length) return false;
    dialog.dataset.pdpDialogActiveState = stateKey;
    items.forEach((item) => {
      const matches = matchingItems.includes(item);
      item.hidden = !matches;
      item.dataset.pdpActive = String(matches);
      item.setAttribute("aria-hidden", String(!matches));
    });
    all("[data-pdp-dialog-state-select]", dialog).forEach((control) => {
      const selected = dialogStateKey(control) === stateKey;
      control.dataset.pdpSelected = String(selected);
      control.setAttribute("aria-current", String(selected));
      if (control.matches("button,[role='button']")) setBoolean(control, "aria-pressed", selected);
    });
    return true;
  }

  function openDialog(dialog, opener) {
    if (!dialog) return;
    dialogOpeners.set(dialog, opener || document.activeElement);
    // Capture the authored media state for every dialog opening, including
    // dialogs whose opener is unkeyed. Internal media selectors can still
    // change that state and closing must return to the authored baseline.
    captureDialogStateBaseline(dialog);
    const requestedState = dialogStateKey(opener);
    if (requestedState) applyDialogState(dialog, requestedState);
    dialog.dataset.pdpActive = SELECTED;
    dialog.removeAttribute("hidden");
    dialog.setAttribute("aria-hidden", "false");
    setBoolean(opener, "aria-expanded", true);
    if (isNativeDialog(dialog) && typeof dialog.showModal === "function") {
      try {
        if (dialog.open) dialog.close();
        dialog.showModal();
      } catch {
        // Detached or partially captured dialog elements cannot enter the top
        // layer. Keep the bounded custom-dialog fallback usable instead.
        dialog.setAttribute("open", "");
      }
    }
    lockPage(dialog);
    const focusRoot = one("[role='dialog']", dialog) || dialog;
    if (!focusRoot.hasAttribute("tabindex")) focusRoot.setAttribute("tabindex", "-1");
    (activeFocusables(focusRoot)[0] || focusRoot).focus({ preventScroll: true });
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (isNativeDialog(dialog) && dialog.open) {
      try {
        dialog.close();
      } catch {
        dialog.removeAttribute("open");
      }
    }
    dialog.dataset.pdpActive = "false";
    dialog.setAttribute("aria-hidden", "true");
    unlockPage(dialog);
    const opener = dialogOpeners.get(dialog);
    setBoolean(opener, "aria-expanded", false);
    restoreDialogStateBaseline(dialog);
    opener?.focus?.({ preventScroll: true });
  }

  function annotateOptions() {
    all("[data-pdp-option-group], .product-form__input, .c-variant-group").forEach((group) => {
      mark(group, "data-pdp-option-group");
      const controls = all("[data-pdp-option], input[type='radio'], input[type='checkbox'], option, button[data-value], .c-variant-option, .c-variant-size-option", group);
      controls.forEach((option) => mark(option, "data-pdp-option", option.value || option.dataset.value || option.dataset.optionValue || ""));
      if (!group.hasAttribute("data-pdp-interaction-id") && !group.id) {
        const owner = group.closest("variant-radios, variant-selects");
        const siblingGroups = owner ? all(".product-form__input", owner) : [];
        const stableSourceId = owner?.id && siblingGroups.length === 1
          ? owner.id
          : controls.find((control) => control.id)?.id || "";
        if (stableSourceId) {
          group.dataset.pdpInteractionId = stableSourceId;
          // The worker may safely discover this exact source id after proving
          // the control across both viewports. It is not an authored inventory
          // binding, so keep that provenance explicit instead of making the
          // validator reject the runtime-created hook as a mismatched one.
          group.dataset.pdpRuntimeDerivedIdentity = "source-id";
        }
      }
      syncOptionGroup(group);
      annotateOptionStateTargets(group);
      applyOptionSelectionDownstream(group, selectedOptionControl(group));
    });
  }

  function isOptionSelected(control) {
    if (!control) return false;
    if (control.matches("select")) return Boolean(control.value);
    if (control.matches("option")) return control.selected;
    if (control.matches("input")) return control.checked;
    return control.getAttribute("aria-pressed") === "true"
      || control.getAttribute("aria-checked") === "true"
      || control.getAttribute("aria-selected") === "true"
      || control.dataset.pdpSelected === SELECTED
      || control.classList.contains("is-active")
      || control.classList.contains("kaching-bundles__bar--selected");
  }

  function syncOptionGroup(group) {
    const controls = all("input, select, [data-pdp-option]", group);
    let value = "";
    controls.forEach((control) => {
      const selected = isOptionSelected(control);
      if (selected) value = control.value || control.dataset.value || control.dataset.pdpOption || value;
      if (control.hasAttribute("data-pdp-option")) {
        control.dataset.pdpSelected = String(selected);
        if (control.matches("button, [role='button']")) setBoolean(control, "aria-pressed", selected);
        if (control.matches("[role='radio'], .kaching-bundles__bar") || control.hasAttribute("aria-checked")) {
          setBoolean(control, "aria-checked", selected);
        }
      }
    });
    group.dataset.pdpValue = value;
  }

  function selectedOptionControl(group, changedControl = null) {
    if (changedControl instanceof HTMLSelectElement) {
      return changedControl.selectedOptions?.[0] || changedControl;
    }
    if (changedControl?.hasAttribute?.("data-pdp-option")) return changedControl;
    return all("[data-pdp-option]", group).find(isOptionSelected) || null;
  }

  function parseOptionState(option) {
    const raw = option?.getAttribute?.("data-pdp-option-state");
    if (raw == null) return { declared: false, targets: [], error: "" };
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) || !Array.isArray(parsed.targets) || !parsed.targets.length) {
        throw new Error('Expected a non-empty {"targets": [...]} object.');
      }
      const targets = parsed.targets.map((target) => {
        if (!target || typeof target !== "object" || Array.isArray(target)) {
          throw new Error("Every option-state target must be an object.");
        }
        const selector = String(target.selector || "").trim();
        if (!selector) throw new Error("Every option-state target needs a selector.");
        const scope = String(target.scope || "auto").trim().toLowerCase();
        if (!["auto", "form", "group", "document"].includes(scope)) {
          throw new Error(`Unsupported option-state scope: ${scope}`);
        }
        const hasText = Object.prototype.hasOwnProperty.call(target, "text");
        const hasHidden = Object.prototype.hasOwnProperty.call(target, "hidden");
        const hasValue = Object.prototype.hasOwnProperty.call(target, "value");
        const hasChecked = Object.prototype.hasOwnProperty.call(target, "checked");
        const hasDisabled = Object.prototype.hasOwnProperty.call(target, "disabled");
        const hasAttributes = Object.prototype.hasOwnProperty.call(target, "attributes");
        if (!hasText && !hasHidden && !hasValue && !hasChecked && !hasDisabled && !hasAttributes) {
          throw new Error(`Option-state target ${selector} has no declared state.`);
        }
        if (hasHidden && typeof target.hidden !== "boolean") throw new Error(`Target ${selector} hidden state must be boolean.`);
        if (hasChecked && typeof target.checked !== "boolean") throw new Error(`Target ${selector} checked state must be boolean.`);
        if (hasDisabled && typeof target.disabled !== "boolean") throw new Error(`Target ${selector} disabled state must be boolean.`);
        if (hasAttributes && (!target.attributes || typeof target.attributes !== "object" || Array.isArray(target.attributes))) {
          throw new Error(`Target ${selector} attributes state must be an object.`);
        }
        const attributes = hasAttributes
          ? Object.fromEntries(Object.entries(target.attributes).map(([name, value]) => {
            const attributeName = String(name || "").trim();
            const safeAttribute = attributeName === "class" || attributeName === "title"
              || attributeName.startsWith("aria-")
              || (attributeName.startsWith("data-") && !attributeName.startsWith("data-pdp-"));
            if (!safeAttribute) {
              throw new Error(`Target ${selector} contains an unsupported attribute name.`);
            }
            return [attributeName, value == null ? null : String(value)];
          }))
          : null;
        return {
          selector,
          scope,
          all: target.all === true,
          ...(hasText ? { text: target.text == null ? "" : String(target.text) } : {}),
          ...(hasHidden ? { hidden: target.hidden } : {}),
          ...(hasValue ? { value: target.value == null ? "" : String(target.value) } : {}),
          ...(hasChecked ? { checked: target.checked } : {}),
          ...(hasDisabled ? { disabled: target.disabled } : {}),
          ...(hasAttributes ? { attributes } : {})
        };
      });
      return { declared: true, targets, error: "" };
    } catch (error) {
      return { declared: true, targets: [], error: error instanceof Error ? error.message : String(error) };
    }
  }

  function optionStateForm(group) {
    return group.closest("form") || group.closest("product-form")?.querySelector("form") || null;
  }

  function mappedStateTargets(group, form, definition) {
    const scopes = definition.scope === "form" ? [form]
      : definition.scope === "group" ? [group]
        : definition.scope === "document" ? [document]
          : [form, group, document];
    for (const scope of scopes.filter((candidate, index, values) => candidate && values.indexOf(candidate) === index)) {
      let matches;
      try {
        matches = all(definition.selector, scope);
      } catch {
        return { targets: [], error: `Invalid selector: ${definition.selector}` };
      }
      if (matches.length) return { targets: definition.all ? matches : [matches[0]], error: "" };
    }
    return { targets: [], error: `No target matched: ${definition.selector}` };
  }

  function annotateOptionStateTargets(group) {
    const form = optionStateForm(group);
    const options = all("[data-pdp-option]", group);
    const stateOptions = options.filter((option) => option.hasAttribute("data-pdp-option-state"));
    const errors = [];
    if (stateOptions.length && stateOptions.length !== options.length) {
      errors.push("Every choice needs a complete option-state snapshot when any choice changes downstream state.");
    }
    stateOptions.forEach((option) => {
      const definition = parseOptionState(option);
      if (definition.error) {
        errors.push(definition.error);
        return;
      }
      definition.targets.forEach((target) => {
        const resolution = mappedStateTargets(group, form, target);
        if (resolution.error) errors.push(resolution.error);
        resolution.targets.forEach((element) => mark(element, "data-pdp-option-dependent"));
      });
    });
    group.dataset.pdpOptionStateContractStatus = errors.length
      ? (errors.some((error) => error.startsWith("No target matched")) ? "missing-targets" : "invalid")
      : stateOptions.length ? "ready" : "not-declared";
    if (errors.length) group.dataset.pdpOptionStateError = errors.join(" | ");
    else delete group.dataset.pdpOptionStateError;
  }

  function applyOptionDownstreamState(group, option) {
    const definition = parseOptionState(option);
    if (!definition.declared) {
      if (group.dataset.pdpOptionStateContractStatus === "not-declared") {
        delete group.dataset.pdpOptionStateStatus;
        delete group.dataset.pdpOptionStateApplied;
      } else {
        group.dataset.pdpOptionStateStatus = "invalid";
        group.dataset.pdpOptionStateApplied = "0";
      }
      return;
    }
    if (definition.error) {
      group.dataset.pdpOptionStateStatus = "invalid";
      group.dataset.pdpOptionStateApplied = "0";
      return;
    }
    const form = optionStateForm(group);
    const failures = [];
    let applied = 0;
    definition.targets.forEach((targetState) => {
      const resolution = mappedStateTargets(group, form, targetState);
      if (resolution.error) failures.push(resolution.error);
      resolution.targets.forEach((target) => {
        let supported = true;
        mark(target, "data-pdp-option-dependent");
        if (Object.prototype.hasOwnProperty.call(targetState, "text")) target.textContent = targetState.text;
        if (Object.prototype.hasOwnProperty.call(targetState, "hidden")) {
          target.hidden = targetState.hidden;
          target.toggleAttribute("hidden", targetState.hidden);
        }
        if (Object.prototype.hasOwnProperty.call(targetState, "value")) {
          if (!("value" in target)) supported = false;
          else {
            target.value = targetState.value;
            if (target.matches("input, textarea")) target.setAttribute("value", targetState.value);
          }
        }
        if (Object.prototype.hasOwnProperty.call(targetState, "checked")) {
          if (!("checked" in target)) supported = false;
          else {
            target.checked = targetState.checked;
            target.toggleAttribute("checked", targetState.checked);
          }
        }
        if (Object.prototype.hasOwnProperty.call(targetState, "disabled")) {
          if (!("disabled" in target)) supported = false;
          else {
            target.disabled = targetState.disabled;
            target.toggleAttribute("disabled", targetState.disabled);
          }
        }
        if (targetState.attributes) {
          Object.entries(targetState.attributes).forEach(([name, value]) => {
            if (value == null) target.removeAttribute(name);
            else target.setAttribute(name, value);
          });
        }
        if (!supported) failures.push(`Matched element cannot represent every declared state: ${targetState.selector}`);
        if (supported) applied += 1;
      });
    });
    const contractStatus = group.dataset.pdpOptionStateContractStatus;
    group.dataset.pdpOptionStateStatus = failures.length ? "missing-targets"
      : contractStatus === "ready" ? "applied" : contractStatus || "invalid";
    group.dataset.pdpOptionStateApplied = String(applied);
  }

  function optionQuantity(option) {
    if (!option) return "";
    const candidates = [option, option.closest?.("[data-quantity]")];
    if (option.id) {
      const escapedId = globalThis.CSS?.escape ? globalThis.CSS.escape(option.id) : "";
      if (escapedId) candidates.push(one(`label[for='${escapedId}'][data-quantity]`));
    }
    const carrier = candidates.find((candidate) => candidate?.hasAttribute?.("data-quantity"));
    const requested = String(carrier?.getAttribute("data-quantity") || "").trim();
    if (!/^\d+$/.test(requested) || integer(requested) < 1) return "";
    return String(integer(requested));
  }

  function quantityInputWithin(scope) {
    if (!scope) return null;
    if (scope.matches?.("input#variant-quantity, input[name='quantity']")) return scope;
    const holder = one("#variant-quantity", scope);
    if (holder?.matches?.("input")) return holder;
    return (holder ? one("input[name='quantity']", holder) : null) || one("input[name='quantity']", scope);
  }

  function optionQuantityInput(group) {
    const form = optionStateForm(group);
    const product = group.closest("product-info, product-form, .product, .product__info-wrapper, [data-product-root]");
    const roots = [form, product, document].filter((candidate, index, values) => (
      candidate && values.indexOf(candidate) === index
    ));
    for (const root of roots) {
      const input = quantityInputWithin(root);
      if (input) return input;
    }
    return null;
  }

  function applyOptionQuantity(group, option) {
    const quantity = optionQuantity(option);
    if (!quantity) {
      delete group.dataset.pdpQuantity;
      return;
    }
    const input = optionQuantityInput(group);
    if (!input) {
      group.dataset.pdpQuantity = quantity;
      group.dataset.pdpQuantityStatus = "missing-input";
      return;
    }
    const changed = String(input.value) !== quantity;
    input.value = quantity;
    input.setAttribute("value", quantity);
    group.dataset.pdpQuantity = quantity;
    group.dataset.pdpQuantityStatus = "applied";
    if (changed) emitInputAndChange(input);
  }

  function applyOptionSelectionDownstream(group, option) {
    applyOptionDownstreamState(group, option);
    applyOptionQuantity(group, option);
  }

  function annotateBundles() {
    all("[data-pdp-option-group]").forEach((group) => {
      group.removeAttribute("data-pdp-bundle-group");
      all("[data-pdp-bundle-offer],[data-pdp-bundle-form-state]", group).forEach((child) =>
        ["data-pdp-bundle-offer", "data-pdp-bundle-form-state"].forEach((name) => child.removeAttribute(name)));
    });
    const groups = all("[data-pdp-bundle-group], .rapi-bundles")
      .filter((group) => !group.hasAttribute("data-pdp-option-group"));
    all(".kaching-bundles__bars").forEach((group) => {
      if (group.hasAttribute("data-pdp-option-group")) return;
      if ([...group.children].some((child) => child.matches?.(".kaching-bundles__bar")) && !groups.includes(group)) groups.push(group);
    });
    groups.forEach((group) => {
      mark(group, "data-pdp-bundle-group");
      const offers = all(":scope > .rapi-bundles__bars > .rapi-bundles__bar, :scope > .kaching-bundles__bar, [data-pdp-bundle-offer]", group);
      const capturedKachingBlock = group.closest(".kaching-bundles__block");
      if (capturedKachingBlock && offers.length) {
        capturedKachingBlock.hidden = false;
        capturedKachingBlock.removeAttribute("aria-hidden");
        if (getComputedStyle(capturedKachingBlock).display === "none") capturedKachingBlock.style.display = "block";
      }
      offers.forEach((offer) => {
        mark(offer, "data-pdp-bundle-offer");
        if (!offer.hasAttribute("data-pdp-bundle-form-state") && offer.matches(".kaching-bundles__bar")) {
          const radio = one("input[type='radio']", offer);
          const escapedId = radio?.id && globalThis.CSS?.escape ? globalThis.CSS.escape(radio.id) : "";
          offer.setAttribute("data-pdp-bundle-form-state", JSON.stringify({
            fields: escapedId ? [{ selector: `#${escapedId}`, scope: "document", checked: true }] : []
          }));
        }
        const variants = one("[data-pdp-bundle-variants], .rapi-bundles__bar-variants", offer);
        mark(variants, "data-pdp-bundle-variants");
      });
      const selected = offers.find((offer) => one("input[type='radio']", offer)?.checked
        || offer.dataset.pdpSelected === SELECTED
        || offer.classList.contains("kaching-bundles__bar--selected")) || offers[0];
      const activeContainer = selected && one(".rapi-bundles__bar-container", selected);
      const inactiveContainer = offers.filter((offer) => offer !== selected).map((offer) => one(".rapi-bundles__bar-container", offer)).find(Boolean);
      bundleStyles.set(group, {
        active: activeContainer ? {
          boxShadow: activeContainer.style.boxShadow,
          backgroundColor: activeContainer.style.backgroundColor,
          borderColor: activeContainer.style.getPropertyValue("--bar-border-color")
        } : null,
        inactive: inactiveContainer ? {
          boxShadow: inactiveContainer.style.boxShadow,
          backgroundColor: inactiveContainer.style.backgroundColor,
          borderColor: inactiveContainer.style.getPropertyValue("--bar-border-color")
        } : null
      });
      if (selected) selectBundle(group, selected, false);
    });
  }

  function applyBundleVisual(container, values) {
    if (!container || !values) return;
    if (values.boxShadow) container.style.boxShadow = values.boxShadow;
    if (values.backgroundColor) container.style.backgroundColor = values.backgroundColor;
    if (values.borderColor) container.style.setProperty("--bar-border-color", values.borderColor);
  }

  function bundleForm(group) {
    return group.closest("form") || group.closest("product-form")?.querySelector("form") || null;
  }

  function parseBundleFormState(offer) {
    const raw = offer?.getAttribute("data-pdp-bundle-form-state");
    if (raw == null) return { declared: false, fields: [], error: "" };
    try {
      const parsed = JSON.parse(raw);
      const sourceFields = Array.isArray(parsed?.fields)
        ? parsed.fields
        : parsed && typeof parsed === "object" && !Array.isArray(parsed)
          ? Object.entries(parsed).map(([selector, state]) => ({
            ...(state && typeof state === "object" && !Array.isArray(state) ? state : { value: state }),
            selector
          }))
          : null;
      if (!sourceFields) return { declared: true, fields: [], error: "Expected a JSON object or a {\"fields\": [...]} object." };
      const fields = sourceFields.map((field) => {
        if (!field || typeof field !== "object" || Array.isArray(field)) throw new Error("Every field mapping must be an object.");
        const selector = String(field.selector || "").trim();
        if (!selector) throw new Error("Every field mapping needs a selector.");
        const scope = String(field.scope || "auto").trim().toLowerCase();
        if (!["auto", "form", "group", "document"].includes(scope)) throw new Error(`Unsupported mapping scope: ${scope}`);
        const hasValue = Object.prototype.hasOwnProperty.call(field, "value");
        const hasChecked = Object.prototype.hasOwnProperty.call(field, "checked");
        const hasDisabled = Object.prototype.hasOwnProperty.call(field, "disabled");
        if (!hasValue && !hasChecked && !hasDisabled) throw new Error(`Mapping ${selector} has no value, checked, or disabled state.`);
        if (hasChecked && typeof field.checked !== "boolean") throw new Error(`Mapping ${selector} checked state must be boolean.`);
        if (hasDisabled && typeof field.disabled !== "boolean") throw new Error(`Mapping ${selector} disabled state must be boolean.`);
        return {
          selector,
          scope,
          all: field.all === true,
          ...(hasValue ? { value: field.value == null ? "" : String(field.value) } : {}),
          ...(hasChecked ? { checked: field.checked } : {}),
          ...(hasDisabled ? { disabled: field.disabled } : {})
        };
      });
      return { declared: true, fields, error: "" };
    } catch (error) {
      return { declared: true, fields: [], error: error instanceof Error ? error.message : String(error) };
    }
  }

  function applyGenericBundleFormState(group, selectedOffer, form) {
    const definition = parseBundleFormState(selectedOffer);
    if (!definition.declared) {
      delete group.dataset.pdpBundleFormStateStatus;
      delete group.dataset.pdpBundleFormStateApplied;
      return;
    }
    if (definition.error) {
      group.dataset.pdpBundleFormStateStatus = "invalid";
      group.dataset.pdpBundleFormStateApplied = "0";
      return;
    }
    const failures = [];
    let applied = 0;
    definition.fields.forEach((field) => {
      const resolution = mappedStateTargets(group, form, field);
      if (resolution.error) failures.push(resolution.error);
      resolution.targets.forEach((target) => {
        let supported = true;
        if (Object.prototype.hasOwnProperty.call(field, "value")) {
          if (!("value" in target)) supported = false;
          else {
            target.value = field.value;
            if (target.matches("input, textarea")) target.setAttribute("value", field.value);
          }
        }
        if (Object.prototype.hasOwnProperty.call(field, "checked")) {
          if (!("checked" in target)) supported = false;
          else {
            target.checked = field.checked;
            target.toggleAttribute("checked", field.checked);
          }
        }
        if (Object.prototype.hasOwnProperty.call(field, "disabled")) {
          if (!("disabled" in target)) supported = false;
          else {
            target.disabled = field.disabled;
            target.toggleAttribute("disabled", field.disabled);
          }
        }
        if (!supported) failures.push(`Matched element cannot represent every declared state: ${field.selector}`);
        target.dispatchEvent(new Event("input", { bubbles: true }));
        emitChange(target);
        if (supported) applied += 1;
      });
    });
    group.dataset.pdpBundleFormStateStatus = failures.length ? "missing-targets" : "applied";
    group.dataset.pdpBundleFormStateApplied = String(applied);
  }

  function applyBundleDownstreamState(group, selectedOffer) {
    const form = bundleForm(group);
    const quantity = selectedOffer.dataset.pdpBundleQuantity;
    const offerValue = selectedOffer.dataset.pdpBundleOfferValue;
    const variantId = selectedOffer.dataset.pdpBundleVariantId;
    const updates = [
      ["input[name='quantity']", quantity],
      ["input[name='properties[__rapi_offer]']", offerValue],
      ["input[name='id'], input.product-variant-id", variantId]
    ];
    updates.forEach(([selector, value]) => {
      if (!form || value == null || value === "") return;
      const target = one(selector, form);
      if (!target) return;
      target.value = value;
      target.setAttribute("value", value);
      emitChange(target);
    });
    applyGenericBundleFormState(group, selectedOffer, form);
    group.dataset.pdpBundleQuantity = quantity || "";
    group.dataset.pdpBundleOfferValue = offerValue || "";
    group.dataset.pdpBundleVariantId = variantId || "";
  }

  function selectBundle(group, selectedOffer, focus = true) {
    const offers = all("[data-pdp-bundle-offer]", group);
    const styles = bundleStyles.get(group) || {};
    offers.forEach((offer) => {
      const selected = offer === selectedOffer;
      offer.dataset.pdpSelected = String(selected);
      if (offer.matches(".kaching-bundles__bar")) offer.classList.toggle("kaching-bundles__bar--selected", selected);
      const radio = one("input[type='radio']", offer);
      if (radio) radio.checked = selected;
      const container = one(".rapi-bundles__bar-container", offer);
      applyBundleVisual(container, selected ? styles.active : styles.inactive);
      const variants = one("[data-pdp-bundle-variants]", offer);
      if (variants) {
        variants.dataset.pdpActive = String(selected);
        variants.style.display = selected ? "block" : "none";
        setBoolean(variants, "aria-hidden", !selected);
      }
    });
    group.dataset.pdpSelected = String(Math.max(0, offers.indexOf(selectedOffer)));
    applyBundleDownstreamState(group, selectedOffer);
    if (focus) (one("label, button, input", selectedOffer) || selectedOffer).focus?.({ preventScroll: true });
  }

  function annotateDropdowns() {
    all("[data-pdp-dropdown], .rapi-dropdown").forEach((dropdown) => {
      mark(dropdown, "data-pdp-dropdown");
      const trigger = one("[data-pdp-dropdown-trigger], .rapi-dropdown-button", dropdown);
      const menu = one("[data-pdp-dropdown-menu], .rapi-dropdown-content", dropdown);
      const value = one("[data-pdp-dropdown-value], .rapi-select-value", dropdown);
      mark(trigger, "data-pdp-dropdown-trigger");
      mark(menu, "data-pdp-dropdown-menu");
      mark(value, "data-pdp-dropdown-value");
      all("[data-pdp-dropdown-choice], .rapi-dropdown-content-single-option", dropdown)
        .forEach((choice) => mark(choice, "data-pdp-dropdown-choice", choice.dataset.value || choice.textContent.trim()));
      setDropdown(dropdown, false, false);
    });
  }

  function setDropdown(dropdown, open, focus = false) {
    const trigger = one("[data-pdp-dropdown-trigger]", dropdown);
    const menu = one("[data-pdp-dropdown-menu]", dropdown);
    dropdown.dataset.pdpActive = String(open);
    setBoolean(trigger, "aria-expanded", open);
    if (menu) {
      menu.classList.toggle("rapi-d-none", !open);
      menu.dataset.pdpActive = String(open);
      setBoolean(menu, "aria-hidden", !open);
    }
    if (focus) trigger?.focus({ preventScroll: true });
  }

  function chooseDropdown(dropdown, choice) {
    const value = choice.dataset.value || choice.getAttribute("data-pdp-dropdown-choice") || choice.textContent.trim();
    const input = one("[data-pdp-dropdown-value]", dropdown);
    const label = one("[data-pdp-dropdown-label], .rapi-dropdown-text", dropdown);
    const displaySwatch = one("[data-pdp-dropdown-swatch], .rapi-dropdown-img", dropdown);
    const choiceSwatch = one("[data-pdp-dropdown-choice-swatch], .rapi-dropdown-content-swatch", choice);
    if (input) {
      input.value = value;
      input.setAttribute("value", value);
      emitChange(input);
    }
    if (label) label.textContent = value;
    if (displaySwatch && choiceSwatch) {
      displaySwatch.style.cssText = choiceSwatch.style.cssText;
      displaySwatch.className = displaySwatch.className;
    }
    all("[data-pdp-dropdown-choice]", dropdown).forEach((item) => {
      const selected = item === choice;
      item.dataset.pdpSelected = String(selected);
      setBoolean(item, "aria-selected", selected);
    });
    dropdown.dataset.pdpSelected = value;
    setDropdown(dropdown, false, true);
  }

  function carouselViewport(root, track = one(carouselTrackSelector, root)) {
    return track?.closest(".splide__track, .slick-list, .swiper") || track?.parentElement || root;
  }

  function carouselOriginalStyles(root, track, slides) {
    const viewport = carouselViewport(root, track);
    return {
      viewport,
      viewportPaddingLeft: viewport?.style.paddingLeft || "",
      viewportPaddingRight: viewport?.style.paddingRight || "",
      trackDisplay: track?.style.display || "",
      trackGap: track?.style.gap || "",
      trackTransform: track?.style.transform || "",
      trackWidth: track?.style.width || "",
      trackJustifyContent: track?.style.justifyContent || "",
      slides: slides.map((slide) => ({
        slide,
        flex: slide.style.flex,
        width: slide.style.width,
        display: slide.style.display,
        order: slide.style.order
      }))
    };
  }

  function restoreCarouselLayout(root) {
    const state = carouselStates.get(root);
    const original = state?.originalStyles;
    if (!original) return;
    if (original.viewport) {
      original.viewport.style.paddingLeft = original.viewportPaddingLeft;
      original.viewport.style.paddingRight = original.viewportPaddingRight;
    }
    const track = one("[data-pdp-carousel-track]", root);
    if (track) {
      track.style.display = original.trackDisplay;
      track.style.gap = original.trackGap;
      track.style.transform = original.trackTransform;
      track.style.width = original.trackWidth;
      track.style.justifyContent = original.trackJustifyContent;
    }
    original.slides.forEach(({ slide, flex, width, display, order }) => {
      slide.style.flex = flex;
      slide.style.width = width;
      slide.style.display = display;
      slide.style.order = order;
    });
  }

  function cssLength(value, context) {
    const raw = String(value || "").trim();
    if (!raw || raw === "normal") return null;
    if (/^-?\d+(?:\.\d+)?(?:px)?$/i.test(raw)) return Number.parseFloat(raw);
    const probe = document.createElement("span");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = `position:absolute;display:block;visibility:hidden;pointer-events:none;width:${raw};height:0;`;
    (context || document.body || html).append(probe);
    const pixels = probe.getBoundingClientRect().width;
    probe.remove();
    return Number.isFinite(pixels) ? pixels : null;
  }

  function responsiveCarouselSetting(root, name, mobile, context = root) {
    const suffix = mobile ? "Mobile" : "Desktop";
    const dataValue = root.dataset[`${name}${suffix}`] ?? root.dataset[name];
    if (dataValue != null && String(dataValue).trim() !== "") return cssLength(dataValue, context);
    const cssName = name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
    const mode = mobile ? "mobile" : "desktop";
    const style = getComputedStyle(root);
    for (const property of [
      `--${cssName}-${mode}`,
      `--pdp-carousel-${cssName}-${mode}`,
      `--splide-${cssName}-${mode}`,
      `--${cssName}`,
      `--pdp-carousel-${cssName}`,
      `--splide-${cssName}`
    ]) {
      const value = style.getPropertyValue(property);
      if (value.trim()) return cssLength(value, context);
    }
    return null;
  }

  function capturedSlidesPerView(value) {
    const match = String(value || "").match(/100%\s*\/\s*(\d+(?:\.\d+)?)/i);
    const parsed = Number.parseFloat(match?.[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function seedGemPagesCarouselSettings(root, slides) {
    if (!root.matches("gp-carousel-v3") || !slides.length) return;
    const slide = slides[0];
    // Restore GemPages' full-width mobile feature image; serialized desktop
    // thumbnail variables and pixel heights otherwise survive the breakpoint.
    const productFeatureGallery = root.classList.contains("gp-feature-image-carousel")
      || Boolean(root.closest("gp-product-images-v3") && root.classList.contains("gp-product-images-gallery"));
    if (productFeatureGallery) {
      root.dataset.slidesMobile = "1";
      root.dataset.slidesDesktop = "1";
      root.dataset.capturedSlideWidthMobile = "100%";
      root.dataset.capturedSlideWidthDesktop = "100%";
    }
    const mobileSlides = capturedSlidesPerView(
      slide.style.getPropertyValue("--minw-mobile")
        || slide.style.getPropertyValue("--maxw-mobile")
        || slide.style.getPropertyValue("--minw-tablet")
    );
    const desktopSlides = capturedSlidesPerView(
      slide.style.getPropertyValue("--minw") || slide.style.getPropertyValue("--maxw")
    );
    if (mobileSlides != null && !root.dataset.slidesMobile) root.dataset.slidesMobile = String(mobileSlides);
    if (desktopSlides != null && !root.dataset.slidesDesktop) root.dataset.slidesDesktop = String(desktopSlides);
    const mobileWidth = slide.style.getPropertyValue("--minw-mobile")
      || slide.style.getPropertyValue("--maxw-mobile")
      || slide.style.getPropertyValue("--minw-tablet")
      || slide.style.getPropertyValue("--maxw-tablet");
    const desktopWidth = slide.style.getPropertyValue("--minw") || slide.style.getPropertyValue("--maxw");
    if (mobileWidth.trim() && !root.dataset.capturedSlideWidthMobile) {
      root.dataset.capturedSlideWidthMobile = mobileWidth.trim();
    }
    if (desktopWidth.trim() && !root.dataset.capturedSlideWidthDesktop) {
      root.dataset.capturedSlideWidthDesktop = desktopWidth.trim();
    }
    // GemPages expresses spacing as each slide's --mr / margin-right. Do not
    // also seed a track gap or the offline carousel doubles that spacing.
    try {
      const settings = JSON.parse(root.getAttribute("gp-data") || "{}").setting || {};
      const loop = settings.loop?.desktop;
      if (loop != null && !root.hasAttribute("data-loop")) root.dataset.loop = String(Boolean(loop));
      if (settings.autoplay != null && !root.hasAttribute("data-autoplay")) root.dataset.autoplay = String(Boolean(settings.autoplay));
      if (settings.autoplayTimeout != null && !root.hasAttribute("data-autoplay-speed")) {
        root.dataset.autoplaySpeed = String(settings.autoplayTimeout);
      }
    } catch {}
  }

  function capturedAspectRatio(element, mobile) {
    const media = one("img, video", element);
    if (!media) return null;
    const style = getComputedStyle(media);
    for (const raw of [
      media.style.getPropertyValue(mobile ? "--aspect-mobile" : "--aspect"),
      media.style.getPropertyValue("--aspect"),
      style.aspectRatio
    ]) {
      const match = String(raw || "").trim().match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
      if (match && Number(match[2]) > 0) return Number(match[1]) / Number(match[2]);
      const numeric = Number.parseFloat(raw);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
    }
    return null;
  }

  function carouselSourceEnabled(root, kind) {
    const aliases = kind === "pagination" ? ["pagination", "dots"] : [kind];
    for (const alias of aliases) {
      if (!root.hasAttribute(`data-${alias}`)) continue;
      const value = String(root.getAttribute(`data-${alias}`) || "true").trim().toLowerCase();
      return !["false", "0", "off", "no"].includes(value);
    }
    if (kind === "arrows") {
      return Boolean(one(`.splide__arrows, .slider-buttons, ${carouselPreviousSelector}, ${carouselNextSelector}`, root)
        || root.hasAttribute("data-arrows-color"));
    }
    return Boolean(one(`.splide__pagination, .swiper-pagination, .slick-dots, .slider-counter, ${carouselPageSelector}`, root)
      || root.hasAttribute("data-dots-color"));
  }

  function carouselArrow(direction) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `splide__arrow splide__arrow--${direction}`;
    button.dataset.pdpGeneratedCarouselControl = direction;
    button.setAttribute("aria-label", direction === "prev" ? "Previous slide" : "Next slide");
    button.innerHTML = '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M8.5 4.5 16 12l-7.5 7.5" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/></svg>';
    mark(button, direction === "prev" ? "data-pdp-carousel-prev" : "data-pdp-carousel-next");
    return button;
  }

  function carouselControlContainer(root, kind) {
    const selector = kind === "arrows"
      ? ".splide__arrows, .slider-buttons"
      : ".gp-carousel-dot-container, .splide__pagination, .swiper-pagination, .slick-dots, .slider-counter";
    let container = one(selector, root);
    if (container) return container;
    container = document.createElement(kind === "arrows" ? "div" : "ul");
    container.className = kind === "arrows" ? "splide__arrows splide__arrows--ltr" : "splide__pagination splide__pagination--ltr";
    container.dataset.pdpGeneratedCarouselContainer = kind;
    if (kind === "pagination") {
      container.setAttribute("role", "tablist");
      container.setAttribute("aria-label", "Carousel pagination");
    }
    (one(".splide", root) || root).append(container);
    return container;
  }

  function appendCarouselPage(container, index, slides) {
    const nativeGemPage = one(".gp-carousel-dot", container);
    const button = nativeGemPage ? nativeGemPage.cloneNode(false) : document.createElement("button");
    button.type = "button";
    if (!nativeGemPage) button.className = "splide__pagination__page";
    else {
      [...button.attributes].forEach(({ name }) => {
        if (name.startsWith("data-pdp-")) button.removeAttribute(name);
      });
      button.removeAttribute("id");
      button.removeAttribute("aria-current");
      button.removeAttribute("aria-selected");
      button.classList.remove("gp-carousel-dot--active", "gem-slider-dot-active", "is-active", "active", "selected");
      button.classList.add("gem-slider-dot-inactive");
      button.style.backgroundColor = "var(--g-c-bg-1, bg-1)";
      button.setAttribute("data-index", String(index));
    }
    button.dataset.pdpGeneratedCarouselControl = "page";
    button.setAttribute("role", "tab");
    button.setAttribute("aria-label", `Go to slide ${index + 1}`);
    const slide = slides[index];
    if (slide) {
      if (!slide.id) slide.id = `pdp-carousel-slide-${Math.random().toString(36).slice(2, 9)}`;
      button.setAttribute("aria-controls", slide.id);
    }
    mark(button, "data-pdp-carousel-page", String(index));
    if (container.matches("ul, ol")) {
      const item = document.createElement("li");
      item.setAttribute("role", "presentation");
      item.append(button);
      container.append(item);
    } else container.append(button);
  }

  function removeGeneratedCarouselControls(root, kind) {
    all(`[data-pdp-generated-carousel-control='${kind}']`, root).forEach((control) => {
      const item = control.parentElement?.matches("li") ? control.parentElement : control;
      item.remove();
    });
    const container = one(`[data-pdp-generated-carousel-container='${kind === "page" ? "pagination" : "arrows"}']`, root);
    if (container && !container.children.length) container.remove();
  }

  function syncCarouselControls(root) {
    const state = carouselStates.get(root);
    const slides = all("[data-pdp-carousel-slide]", root);
    if (!slides.length) return;
    const disabled = Boolean(state?.layout?.disabled);
    if (!carouselSourceEnabled(root, "arrows")) {
      removeGeneratedCarouselControls(root, "prev");
      removeGeneratedCarouselControls(root, "next");
    } else if (!disabled) {
      const arrows = carouselControlContainer(root, "arrows");
      if (!one(carouselPreviousSelector, root)) arrows.append(carouselArrow("prev"));
      if (!one(carouselNextSelector, root)) arrows.append(carouselArrow("next"));
    }
    mark(one(carouselPreviousSelector, root), "data-pdp-carousel-prev");
    mark(one(carouselNextSelector, root), "data-pdp-carousel-next");

    if (!carouselSourceEnabled(root, "pagination")) removeGeneratedCarouselControls(root, "page");
    else if (!disabled) {
      // GemPages v3 renders one native dot per slide even when several slides
      // are visible at once. Preserve that authored contract instead of
      // applying the generic "number of scroll positions" calculation.
      const positions = root.matches("gp-carousel-v3") || root.dataset.loop === SELECTED
        ? slides.length
        : Math.ceil(Math.max(1, slides.length - carouselPerPage(root) + 1));
      let pagination = carouselControlContainer(root, "pagination");
      let pages = all(carouselPageSelector, root);
      const generatedOnly = pages.length > 0 && pages.every((page) => page.dataset.pdpGeneratedCarouselControl === "page");
      if (generatedOnly && pages.length !== positions) {
        removeGeneratedCarouselControls(root, "page");
        pages = [];
        pagination = carouselControlContainer(root, "pagination");
      }
      for (let index = pages.length; index < positions; index += 1) appendCarouselPage(pagination, index, slides);
    }
    all(carouselPageSelector, root)
      .forEach((page, index) => mark(page, "data-pdp-carousel-page", String(index)));
    all("[data-pdp-generated-carousel-control], [data-pdp-generated-carousel-container]", root)
      .forEach((control) => {
        control.hidden = disabled;
        if (control.dataset.pdpGeneratedCarouselControl === "page" && control.parentElement?.matches("li")) {
          control.parentElement.hidden = disabled;
        }
      });
  }

  function configureCarouselLayout(root) {
    const state = carouselStates.get(root);
    const track = one("[data-pdp-carousel-track]", root);
    const slides = all("[data-pdp-carousel-slide]", root);
    if (!state || !track || !slides.length) return;
    restoreCarouselLayout(root);
    const mobile = matchMedia("(max-width: 749px)").matches;
    const disabled = mobile
      ? root.dataset.destroyMobile === SELECTED
      : root.dataset.destroyDesktop === SELECTED;
    if (disabled) {
      track.style.display = "flex";
      track.style.transform = "none";
      track.style.width = "100%";
      track.style.justifyContent = "space-between";
      slides.forEach((slide) => {
        slide.style.flex = "1 1 auto";
        slide.style.width = "auto";
      });
      state.layout = { disabled: true, mobile, perPage: slides.length, gap: 0, sidePadding: 0 };
      root.dataset.pdpCarouselLayout = "source";
      syncCarouselControls(root);
      return;
    }

    const viewport = carouselViewport(root, track);
    const requestedPerPage = responsiveCarouselSetting(root, "slides", mobile);
    const parsedPerPage = Number.parseFloat(String(requestedPerPage ?? ""));
    const perPage = Math.max(1, Math.min(slides.length, Number.isFinite(parsedPerPage) ? parsedPerPage : 1));
    const requestedGap = responsiveCarouselSetting(root, "gap", mobile, track);
    const requestedPadding = responsiveCarouselSetting(root, "sidePadding", mobile, viewport);
    if (requestedGap != null) track.style.gap = `${Math.max(0, requestedGap)}px`;
    if (requestedPadding != null && viewport) {
      viewport.style.paddingLeft = `${Math.max(0, requestedPadding)}px`;
      viewport.style.paddingRight = `${Math.max(0, requestedPadding)}px`;
    }
    track.style.display = "flex";
    track.style.transform = "none";
    track.style.width = "100%";
    track.style.justifyContent = "flex-start";
    const trackStyle = getComputedStyle(track);
    const viewportStyle = getComputedStyle(viewport);
    const gap = Math.max(0, requestedGap ?? cssLength(trackStyle.columnGap, track) ?? 0);
    const paddingLeft = Math.max(0, cssLength(viewportStyle.paddingLeft, viewport) ?? 0);
    const paddingRight = Math.max(0, cssLength(viewportStyle.paddingRight, viewport) ?? 0);
    const borderLeft = Math.max(0, cssLength(viewportStyle.borderLeftWidth, viewport) ?? 0);
    const borderRight = Math.max(0, cssLength(viewportStyle.borderRightWidth, viewport) ?? 0);
    const viewportWidth = viewport.getBoundingClientRect().width || viewport.clientWidth || root.getBoundingClientRect().width;
    const available = Math.max(0, viewportWidth - paddingLeft - paddingRight - borderLeft - borderRight - gap * (perPage - 1));
    const capturedSlideWidth = root.matches("gp-carousel-v3")
      ? cssLength(root.dataset[mobile ? "capturedSlideWidthMobile" : "capturedSlideWidthDesktop"], track)
      : null;
    const slideWidth = capturedSlideWidth != null && capturedSlideWidth > 0
      ? capturedSlideWidth
      : available / perPage;
    slides.forEach((slide) => {
      slide.style.display = "block";
      slide.style.flex = `0 0 ${slideWidth}px`;
      slide.style.width = `${slideWidth}px`;
    });
    if (root.classList.contains("gp-feature-image-carousel")) {
      slides.forEach((slide) => {
        const ratio = capturedAspectRatio(slide, mobile);
        const renderedWidth = slide.getBoundingClientRect().width || slideWidth;
        if (ratio && renderedWidth > 0) slide.style.height = `${renderedWidth / ratio}px`;
      });
    }
    state.layout = { disabled: false, mobile, perPage, gap, sidePadding: Math.max(paddingLeft, paddingRight), slideWidth };
    root.dataset.pdpCarouselLayout = mobile ? "mobile" : "desktop";
    syncCarouselControls(root);
  }

  function annotateCarousel(root) {
    mark(root, "data-pdp-carousel");
    all(".slick-cloned", root).forEach((clone) => {
      clone.hidden = true;
      clone.style.display = "none";
      clone.setAttribute("aria-hidden", "true");
    });
    const track = one("[data-pdp-carousel-track]", root) || one(carouselTrackSelector, root);
    mark(track, "data-pdp-carousel-track");
    const slides = all(carouselSlideSelector, root).filter(slide => !slide.closest('[data-pdp-carousel-page]'));
    slides.forEach((slide, index) => mark(slide, "data-pdp-carousel-slide", String(index)));
    seedGemPagesCarouselSettings(root, slides);
    mark(one(carouselPreviousSelector, root), "data-pdp-carousel-prev");
    mark(one(carouselNextSelector, root), "data-pdp-carousel-next");
    all(carouselPageSelector, root)
      .forEach((page, index) => mark(page, "data-pdp-carousel-page", String(index)));
    carouselStates.set(root, {
      index: 0,
      autoplayTimer: 0,
      pauseReasons: new Set(),
      originalStyles: carouselOriginalStyles(root, track, slides),
      layout: null
    });
    configureCarouselLayout(root);
    showCarousel(root, 0, false);
    scheduleCarouselAutoplay(root);
  }

  function carouselPerPage(root) {
    const state = carouselStates.get(root);
    if (state?.layout?.perPage) return state.layout.perPage;
    const mobile = matchMedia("(max-width: 749px)").matches;
    const requested = responsiveCarouselSetting(root, "slides", mobile);
    const parsed = Number.parseFloat(String(requested ?? ""));
    return Math.max(1, Number.isFinite(parsed) ? parsed : 1);
  }

  function carouselOffset(slides, index) {
    if (!slides[index]) return 0;
    return slides[index].offsetLeft - slides[0].offsetLeft;
  }

  function showCarousel(root, requestedIndex, focusPage = false) {
    const track = one("[data-pdp-carousel-track]", root);
    const slides = all("[data-pdp-carousel-slide]", root);
    if (!track || !slides.length) return;
    const state = carouselStates.get(root) || {};
    if (state.layout?.disabled) {
      state.index = 0;
      root.dataset.pdpIndex = "0";
      track.style.transform = "none";
      slides.forEach((slide) => {
        slide.dataset.pdpActive = "true";
        slide.dataset.pdpVisible = "true";
        slide.classList.add("is-visible");
        setBoolean(slide, "aria-hidden", false);
      });
      return;
    }
    const perPage = Math.min(slides.length, carouselPerPage(root));
    const visibleCount = Math.min(slides.length, Math.ceil(perPage));
    const loop = root.dataset.loop === SELECTED;
    const max = loop
      ? Math.max(0, slides.length - 1)
      : Math.max(0, Math.ceil(slides.length - perPage));
    const index = loop && max > 0
      ? ((requestedIndex % (max + 1)) + (max + 1)) % (max + 1)
      : Math.max(0, Math.min(max, requestedIndex));
    state.index = index;
    carouselStates.set(root, state);
    root.dataset.pdpIndex = String(index);
    // A looping source carousel has one reachable state per real slide even
    // when several slides are visible at once. Rotate the visual order instead
    // of depending on third-party clone nodes that the offline archive removes.
    // Non-looping carousels retain the bounded translate behavior.
    track.style.transform = loop
      ? "translate3d(0, 0, 0)"
      : `translate3d(${-carouselOffset(slides, index)}px, 0, 0)`;
    slides.forEach((slide, slideIndex) => {
      const active = slideIndex === index;
      const loopDistance = (slideIndex - index + slides.length) % slides.length;
      const visible = loop ? loopDistance < visibleCount : slideIndex >= index && slideIndex < index + visibleCount;
      if (loop) slide.style.order = String(loopDistance);
      slide.dataset.pdpActive = String(active);
      slide.dataset.pdpVisible = String(visible);
      slide.classList.toggle("is-active", active);
      slide.classList.toggle("is-visible", visible);
      setBoolean(slide, "aria-hidden", !visible);
    });
    const previous = one("[data-pdp-carousel-prev]", root);
    const next = one("[data-pdp-carousel-next]", root);
    if (previous) previous.disabled = !loop && index === 0;
    if (next) next.disabled = !loop && index === max;
    all("[data-pdp-carousel-page]", root).forEach((page, pageIndex) => {
      const selected = attributeIndex(page, "data-pdp-carousel-page", pageIndex) === index;
      page.dataset.pdpActive = String(selected);
      page.classList.toggle("is-active", selected);
      if (page.dataset.pdpCarouselActiveClass) page.classList.toggle(page.dataset.pdpCarouselActiveClass, selected);
      setBoolean(page, "aria-selected", selected);
      if (selected) page.setAttribute("aria-current", "true");
      else page.removeAttribute("aria-current");
      if (selected && focusPage) page.focus({ preventScroll: true });
    });
    const visibleHeight = Math.max(...slides
      .slice(index, index + perPage)
      .map((slide) => Math.ceil(slide.getBoundingClientRect().height)));
    if (visibleHeight > 0) root.style.setProperty("--active-slide-height", `${visibleHeight}px`);
  }

  function carouselAutoplayDelay(root) {
    const configured = Number.parseFloat(root.dataset.autoplaySpeed);
    const requested = Number.isFinite(configured)
      ? (configured <= 100 ? configured * 1000 : configured)
      : 5000;
    return Math.max(1000, Math.min(60000, requested));
  }

  function revealHydratedReviewWidgets() {
    // Reveal captured reviews only with completed-setup evidence and content;
    // the provider's startup hiding rule outlives its removed remote script.
    all(".jdgm-review-widget.jdgm--done-setup-widget .jdgm-rev-widg").forEach((widget) => {
      const substantive = String(widget.textContent || "").replace(/\s+/g, " ").trim().length >= 40
        || Boolean(one("img,video", widget));
      if (!substantive) return;
      widget.style.setProperty("display", "block", "important");
      widget.dataset.pdpHydratedContent = "visible";
    });
  }

  function carouselAutoplayAvailable(root) {
    const slides = all("[data-pdp-carousel-slide]", root);
    const perPage = Math.min(slides.length, carouselPerPage(root));
    return root.dataset.autoplay === SELECTED && slides.length > 1 && slides.length - perPage > 0;
  }

  function stopCarouselAutoplay(root) {
    const state = carouselStates.get(root);
    if (!state?.autoplayTimer) return;
    clearTimeout(state.autoplayTimer);
    state.autoplayTimer = 0;
  }

  function scheduleCarouselAutoplay(root) {
    const state = carouselStates.get(root);
    if (!state) return;
    stopCarouselAutoplay(root);
    if (!carouselAutoplayAvailable(root)) {
      root.dataset.pdpAutoplayState = "disabled";
      return;
    }
    if (document.hidden || reducedMotion.matches || state.pauseReasons.size) {
      root.dataset.pdpAutoplayState = "paused";
      return;
    }
    root.dataset.pdpAutoplayState = "running";
    state.autoplayTimer = setTimeout(() => {
      state.autoplayTimer = 0;
      if (!root.isConnected) return;
      const slides = all("[data-pdp-carousel-slide]", root);
      const max = Math.max(0, slides.length - Math.min(slides.length, carouselPerPage(root)));
      const next = state.index >= max ? 0 : state.index + 1;
      showCarousel(root, next, false);
      scheduleCarouselAutoplay(root);
    }, carouselAutoplayDelay(root));
  }

  function pauseCarouselAutoplay(root, reason) {
    const state = carouselStates.get(root);
    if (!state) return;
    state.pauseReasons.add(reason);
    stopCarouselAutoplay(root);
    if (carouselAutoplayAvailable(root)) root.dataset.pdpAutoplayState = "paused";
  }

  function resumeCarouselAutoplay(root, reason) {
    const state = carouselStates.get(root);
    if (!state) return;
    state.pauseReasons.delete(reason);
    scheduleCarouselAutoplay(root);
  }

  function annotateReviews() {
    const roots = all("[data-pdp-review-pages]");
    const venialli = one("#vn-reviews");
    if (venialli && !roots.includes(venialli)) roots.push(venialli);
    roots.forEach((root) => {
      mark(root, "data-pdp-review-pages");
      const pages = all("[data-pdp-review-page], .vn-rev-page", root);
      pages.forEach((page, index) => mark(page, "data-pdp-review-page", page.dataset.page || String(index + 1)));
      let controls = all("[data-pdp-review-control]", root);
      if (!controls.length) {
        controls = all("button", root).filter((button) => /^(?:‹|›|\d+)$/.test(button.textContent.trim()));
        controls.forEach((button) => {
          const text = button.textContent.trim();
          mark(button, "data-pdp-review-control", text === "‹" ? "prev" : text === "›" ? "next" : text);
        });
      }
      const initial = Math.max(0, pages.findIndex((page) => getComputedStyle(page).display !== "none" && !page.hidden));
      showReviewPage(root, initial < 0 ? 0 : initial);
    });
  }

  function showReviewPage(root, requestedIndex) {
    const pages = all("[data-pdp-review-page]", root);
    if (!pages.length) return;
    const index = Math.max(0, Math.min(pages.length - 1, requestedIndex));
    root.dataset.pdpIndex = String(index);
    pages.forEach((page, pageIndex) => {
      const active = pageIndex === index;
      page.dataset.pdpActive = String(active);
      page.hidden = !active;
      page.style.display = active ? "block" : "none";
      setBoolean(page, "aria-hidden", !active);
    });
    all("[data-pdp-review-control]", root).forEach((control) => {
      const command = control.dataset.pdpReviewControl;
      const pageIndex = /^\d+$/.test(command) ? integer(command) - 1 : -1;
      const active = pageIndex === index;
      control.dataset.pdpActive = String(active);
      setBoolean(control, "aria-current", active);
      if (command === "prev") control.disabled = index === 0;
      if (command === "next") control.disabled = index === pages.length - 1;
    });
  }

  function annotateMenus() {
    all("[data-pdp-menu], details.menu-drawer-container").forEach((root) => {
      if (root.matches("details.menu-drawer-container") && root.parentElement?.closest("[data-pdp-menu]")) return;
      mark(root, "data-pdp-menu");
      const trigger = one("[data-pdp-menu-trigger], :scope > summary", root);
      const panel = one("[data-pdp-menu-panel], .menu-drawer", root);
      mark(trigger, "data-pdp-menu-trigger");
      mark(panel, "data-pdp-menu-panel");
      all("[data-pdp-menu-close], .menu-drawer__close-btn", root).forEach((close) => mark(close, "data-pdp-menu-close"));
      setMenu(root, false, false);
    });
  }

  function setMenu(root, open, restoreFocus = true) {
    const trigger = one("[data-pdp-menu-trigger]", root);
    const panel = one("[data-pdp-menu-panel]", root);
    const details = root.matches("details") ? root : panel?.closest("details");
    if (details) details.open = open;
    root.dataset.pdpActive = String(open);
    (root.dataset.pdpMenuRootActiveClasses || '').split(/\s+/).filter(name => /^[a-zA-Z][\w-]*$/.test(name)).forEach(name => root.classList.toggle(name, open));
    panel && (panel.dataset.pdpActive = String(open));
    setBoolean(trigger, "aria-expanded", open);
    setBoolean(panel, "aria-hidden", !open);
    if (panel) {
      panel.hidden = !open;
      if (open && panel.style.display === 'none') panel.style.removeProperty('display');
      const activeClass = panel.dataset.pdpMenuActiveClass;
      if (activeClass && /^[a-zA-Z][\w-]*$/.test(activeClass)) panel.classList.toggle(activeClass, open);
    }
    if (open) {
      lockPage(root);
      (activeFocusables(panel)[0] || panel)?.focus?.({ preventScroll: true });
    } else {
      unlockPage(root);
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }
  }

  function annotateSearch() {
    all("[data-pdp-search], details-modal.header__search").forEach((root) => {
      mark(root, "data-pdp-search");
      const details = one("details", root);
      const trigger = one("[data-pdp-search-trigger]", root) || details?.querySelector(":scope > summary");
      const panel = one("[data-pdp-search-panel], .search-modal", root);
      const input = one("[data-pdp-search-input], input[type='search']", root);
      mark(trigger, "data-pdp-search-trigger");
      mark(panel, "data-pdp-search-panel");
      mark(input, "data-pdp-search-input");
      all("[data-pdp-search-close], .search-modal__close-button", root).forEach((close) => mark(close, "data-pdp-search-close"));
      all("[data-pdp-search-clear], .reset__button", root).forEach((clear) => mark(clear, "data-pdp-search-clear"));
      setSearch(root, false, false);
    });
  }

  function setSearch(root, open, restoreFocus = true) {
    const details = one("details", root);
    const trigger = one("[data-pdp-search-trigger]", root);
    const panels = all("[data-pdp-search-panel]", root);
    if (details) details.open = open;
    root.dataset.pdpActive = String(open);
    panels.forEach((panel) => {
      panel.dataset.pdpActive = String(open);
      setBoolean(panel, "aria-hidden", !open);
      if (panel instanceof HTMLDialogElement) {
        try {
          if (open && !panel.open) panel.showModal();
          else if (!open && panel.open) panel.close();
        } catch {
          panel.toggleAttribute("open", open);
        }
      }
    });
    setBoolean(trigger, "aria-expanded", open);
    if (open) {
      lockPage(root);
      requestAnimationFrame(() => one("[data-pdp-search-input]", root)?.focus({ preventScroll: true }));
    } else {
      unlockPage(root);
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }
  }

  function visibleRatio(element) {
    const rect = element?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return 0;
    const visibleWidth = Math.max(0, Math.min(innerWidth, rect.right) - Math.max(0, rect.left));
    const visibleHeight = Math.max(0, Math.min(innerHeight, rect.bottom) - Math.max(0, rect.top));
    return Math.max(0, Math.min(1, (visibleWidth * visibleHeight) / (rect.width * rect.height)));
  }

  function annotateSticky() {
    all("[data-pdp-sticky], sticky-atc, .c-sticky-atc").forEach((sticky) => {
      mark(sticky, "data-pdp-sticky");
      const capturedViewportSticky = sticky.matches(".c-sticky-atc");
      if (capturedViewportSticky) {
        const mobileQuery = matchMedia("(max-width: 767px)");
        const firstSection = one(".c-custom-first-section");
        const purchaseSentinel = one(".gqy6HHifi-, [data-pdp-sticky-purchase-sentinel]")
          || one(".c-add-to-cart-btn:not(.c-sticky-atc *)")?.closest("[gp-el-wrapper], .c-add-to-cart-section, form");
        let scrollFrame = 0;
        const updateFromViewport = () => {
          cancelAnimationFrame(scrollFrame);
          scrollFrame = requestAnimationFrame(() => {
            if (!mobileQuery.matches) {
              sticky.dataset.pdpVisible = "false";
              return;
            }
            if (firstSection) {
              const firstVisible = visibleRatio(firstSection) >= 0.15;
              const purchaseVisible = purchaseSentinel ? visibleRatio(purchaseSentinel) >= 0.12 : false;
              sticky.dataset.pdpVisible = String(!firstVisible && !purchaseVisible);
              return;
            }
            const sentinel = one("[data-pdp-sticky-sentinel], .c-add-to-cart-btn:not(.c-sticky-atc *), .product-form__submit:not(.c-sticky-atc *)");
            const rect = sentinel?.getBoundingClientRect();
            sticky.dataset.pdpVisible = String(Boolean(rect && rect.bottom < 0));
          });
        };
        sticky.dataset.pdpVisible = "false";
        addEventListener("scroll", updateFromViewport, { passive: true });
        addEventListener("resize", updateFromViewport, { passive: true });
        mobileQuery.addEventListener?.("change", updateFromViewport);
        updateFromViewport();
        return;
      }
      let sentinel = one("[data-pdp-sticky-sentinel]");
      if (!sentinel) sentinel = one(".product-form__submit:not(sticky-atc *), [data-pdp-primary-action]");
      mark(sentinel, "data-pdp-sticky-sentinel");
      sticky.dataset.pdpVisible = "false";
      const update = (entry) => {
        const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        sticky.dataset.pdpVisible = String(passed);
      };
      let scrollFrame = 0;
      const onScroll = () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const rect = sentinel?.getBoundingClientRect();
          if (!rect) return;
          update({ isIntersecting: rect.bottom > 0 && rect.top < innerHeight, boundingClientRect: rect });
        });
      };
      if (sentinel && "IntersectionObserver" in window) {
        const observer = new IntersectionObserver((entries) => entries[0] && update(entries[0]), { threshold: 0 });
        observer.observe(sentinel);
        stickyObservers.push(observer);
      }
      if (sentinel) {
        addEventListener("scroll", onScroll, { passive: true });
        onScroll();
      }
    });
  }

  function hydrateVideoSources(video) {
    let promoted = false;
    if (!video.getAttribute("src")) {
      const capturedSource = video.getAttribute("data-pdp-captured-src") || video.getAttribute("data-src");
      if (capturedSource) {
        video.setAttribute("src", capturedSource);
        promoted = true;
      }
    }
    all("source", video).forEach((source) => {
      if (source.getAttribute("src")) return;
      const capturedSource = source.getAttribute("data-pdp-captured-src") || source.getAttribute("data-src");
      if (capturedSource) {
        source.setAttribute("src", capturedSource);
        promoted = true;
      }
    });
    if (promoted || video.readyState === 0) {
      try { video.load(); } catch {}
    }
  }

  function annotateVideos() {
    all("[data-pdp-video], internal-video, gp-lite-html5-embed").forEach((root) => {
      mark(root, "data-pdp-video");
      const video = one("video", root);
      let toggle = one("[data-pdp-video-toggle], .internal-video__play, .gp-thumbnail-video button[aria-label='Play'], .gp-thumbnail-video button[aria-label='Pause video']", root);
      const requested = root.dataset.autoplay === SELECTED || root.dataset.noPlayBtn === SELECTED;
      const hidden = toggle && (toggle.hidden || toggle.getAttribute("aria-hidden") === SELECTED
        || !toggle.getClientRects().length || getComputedStyle(toggle).visibility === "hidden");
      if (requested && (!toggle || hidden)) {
        toggle?.removeAttribute("data-pdp-video-toggle");
        toggle = null;
      }
      mark(toggle, "data-pdp-video-toggle");
      if (!video) {
        root.dataset.pdpAvailable = "false";
        return;
      }
      const available = Boolean(
        video.currentSrc
        || video.getAttribute("src")
        || video.getAttribute("data-src")
        || video.getAttribute("data-pdp-captured-src")
        || one("source[src], source[data-src], source[data-pdp-captured-src]", video)
      );
      const autoplay = !toggle && (requested || video.autoplay);
      root.dataset.pdpAvailable = String(available);
      root.dataset.pdpVideoMode = autoplay ? "autoplay" : root.dataset.pdpVideoMode || (toggle ? "toggle" : "passive");
      if (autoplay) {
        video.autoplay = video.muted = video.playsInline = true;
      }
      if (toggle) {
        toggle.disabled = !available;
        setBoolean(toggle, "aria-disabled", !available);
      }
      syncVideo(root);
      ["play", "pause", "ended"].forEach((event) => video.addEventListener(event, () => syncVideo(root)));
      if (autoplay && available) {
        hydrateVideoSources(video);
        try { video.play().catch?.(() => syncVideo(root)); } catch { syncVideo(root); }
      }
    });
  }

  function syncVideo(root) {
    const video = one("video", root);
    const toggle = one("[data-pdp-video-toggle]", root);
    if (!video) return;
    const playing = !video.paused && !video.ended;
    root.dataset.pdpPlaying = String(playing);
    root.classList.toggle("internal-video--playing", playing);
    const activeClass = root.dataset.pdpVideoActiveClass;
    if (activeClass && /^[a-zA-Z_][\w-]*$/.test(activeClass)) root.classList.toggle(activeClass, playing);
    if (root.dataset.pdpVideoReveal === SELECTED && playing) {
      video.hidden = false;
      const poster = one("[data-pdp-video-poster]", root);
      if (poster) poster.style.visibility = "hidden";
    }
    setBoolean(toggle, "aria-pressed", playing);
    if (toggle) toggle.setAttribute("aria-label", playing ? "Pause video" : "Play video");
  }

  function countdownNumber(value, fallback = 0) {
    const parsed = Number.parseInt(String(value ?? ""), 10);
    return Number.isSafeInteger(parsed) && parsed >= 0 && parsed <= 31_622_400 ? parsed : fallback;
  }

  function countdownValues(root, remainingSeconds) {
    const hasDays = Boolean(one('[data-pdp-countdown-part="days"]', root));
    const hasHours = Boolean(one('[data-pdp-countdown-part="hours"]', root));
    return {
      days: Math.floor(remainingSeconds / 86_400),
      hours: hasDays ? Math.floor(remainingSeconds / 3_600) % 24 : Math.floor(remainingSeconds / 3_600),
      minutes: hasDays || hasHours ? Math.floor(remainingSeconds / 60) % 60 : Math.floor(remainingSeconds / 60),
      seconds: remainingSeconds % 60,
      total_hours: Math.floor(remainingSeconds / 3_600),
      total_minutes: Math.floor(remainingSeconds / 60),
      total_seconds: remainingSeconds
    };
  }

  function countdownRemaining(state, now = Date.now()) {
    const elapsedSeconds = Math.max(0, Math.floor((now - state.startedAt) / 1000));
    if (elapsedSeconds < state.initialSeconds) return state.initialSeconds - elapsedSeconds;
    if (state.reset !== "loop") return 0;
    const afterInitial = elapsedSeconds - state.initialSeconds;
    if (afterInitial < state.zeroHoldSeconds) return 0;
    const cycleSeconds = state.loopSeconds + state.zeroHoldSeconds;
    const phase = (afterInitial - state.zeroHoldSeconds) % cycleSeconds;
    return phase < state.loopSeconds ? state.loopSeconds - phase : 0;
  }

  function renderCountdown(root, state, remainingSeconds) {
    const values = countdownValues(root, remainingSeconds);
    state.parts.forEach(({ element, part, pad }) => {
      element.textContent = String(values[part]).padStart(pad, "0");
    });
    state.displays.forEach(({ element, template }) => {
      element.textContent = template.replace(/\{(days|hours|minutes|seconds|total_hours|total_minutes|total_seconds)\}/g, (_, part) => String(values[part]).padStart(part === "days" ? 1 : 2, "0"));
    });
    root.dataset.pdpCountdownRemainingSeconds = String(remainingSeconds);
    root.dataset.pdpCountdownState = remainingSeconds > 0 || state.reset === "loop" ? "running" : "finished";
    if (remainingSeconds !== state.lastRemaining) {
      state.tick += 1;
      root.dataset.pdpCountdownTick = String(state.tick);
      state.lastRemaining = remainingSeconds;
    }
  }

  function scheduleCountdown(root) {
    const state = countdownStates.get(root);
    if (!state) return;
    if (state.timer) clearTimeout(state.timer);
    const remaining = countdownRemaining(state);
    renderCountdown(root, state, remaining);
    if (!root.isConnected || (remaining === 0 && state.reset !== "loop")) return;
    const elapsed = Math.max(0, Date.now() - state.startedAt);
    const delay = Math.max(50, Math.min(1000, 1010 - (elapsed % 1000)));
    state.timer = setTimeout(() => scheduleCountdown(root), delay);
  }

  const capturedCountdownPartDefinitions = [
    { selector: "[data-countdown-day]", part: "days", multiplier: 86_400 },
    { selector: "[data-countdown-hr]", part: "hours", multiplier: 3_600 },
    { selector: "[data-countdown-min]", part: "minutes", multiplier: 60 },
    { selector: "[data-countdown-sec]", part: "seconds", multiplier: 1 }
  ];
  const capturedCountdownPartSelector = capturedCountdownPartDefinitions
    .map(({ selector }) => selector)
    .join(",");

  function capturedCountdownRoot(partElement) {
    const fallback = partElement.parentElement;
    let candidate = fallback;
    while (candidate && candidate !== document.body) {
      if (all(capturedCountdownPartSelector, candidate).length >= 2) return candidate;
      candidate = candidate.parentElement;
    }
    return fallback;
  }

  function hasTwelveHourCountdownEvidence(root) {
    let scope = root;
    for (let depth = 0; scope && scope !== document.body && depth < 3; depth += 1) {
      if (scope.matches(".c-countdown-timer-12h") || one(".c-countdown-timer-12h", scope)) return true;
      scope = scope.parentElement;
    }
    return false;
  }

  function annotateCapturedCountdownRoots() {
    const roots = new Set(all(capturedCountdownPartSelector).map(capturedCountdownRoot).filter(Boolean));
    roots.forEach((root) => {
      mark(root, "data-pdp-countdown");
      root.dataset.pdpCountdownSource = root.dataset.pdpCountdownSource || "captured-parts";
      let initialSeconds = 0;
      let partCount = 0;
      capturedCountdownPartDefinitions.forEach(({ selector, part, multiplier }) => {
        const element = one(selector, root);
        if (!element) return;
        partCount += 1;
        mark(element, "data-pdp-countdown-part", part);
        const captured = countdownNumber(element.textContent.match(/\d+/)?.[0], 0);
        initialSeconds += captured * multiplier;
      });
      if (partCount && !root.hasAttribute("data-pdp-countdown-duration-seconds")) {
        root.dataset.pdpCountdownDurationSeconds = String(initialSeconds);
      }
      if (hasTwelveHourCountdownEvidence(root)) {
        if (!root.hasAttribute("data-pdp-countdown-reset")) root.dataset.pdpCountdownReset = "loop";
        if (!root.hasAttribute("data-pdp-countdown-loop-seconds")) root.dataset.pdpCountdownLoopSeconds = "43200";
      }
    });
  }

  function annotateCountdowns() {
    annotateCapturedCountdownRoots();
    all("[data-pdp-countdown]").forEach((root) => {
      const initialSeconds = countdownNumber(root.dataset.pdpCountdownDurationSeconds, -1);
      const reset = root.dataset.pdpCountdownReset === "loop" ? "loop" : "stop";
      const loopSeconds = countdownNumber(root.dataset.pdpCountdownLoopSeconds, initialSeconds);
      const zeroHoldSeconds = countdownNumber(root.dataset.pdpCountdownZeroHoldSeconds, 0);
      const parts = all("[data-pdp-countdown-part]", root).map((element) => {
        const part = String(element.dataset.pdpCountdownPart || "").trim().toLowerCase();
        const requestedPad = countdownNumber(element.dataset.pdpCountdownPad, 0);
        const visibleDigits = (element.textContent.match(/\d+/)?.[0] || "").length;
        return {
          element,
          part,
          pad: Math.max(1, Math.min(6, requestedPad || visibleDigits || (part === "days" ? 1 : 2)))
        };
      }).filter(({ part }) => ["days", "hours", "minutes", "seconds", "total_hours", "total_minutes", "total_seconds"].includes(part));
      const displayElements = [root, ...all("[data-pdp-countdown-display]", root)]
        .filter((element, index, values) => element.hasAttribute("data-pdp-countdown-display") && values.indexOf(element) === index);
      const displays = displayElements.map((element) => ({
        element,
        template: String(element.dataset.pdpCountdownTemplate || "").trim()
      })).filter(({ template }) => /\{(?:days|hours|minutes|seconds|total_hours|total_minutes|total_seconds)\}/.test(template));
      if (initialSeconds < 0 || (reset === "loop" && loopSeconds <= 0) || (!parts.length && !displays.length)) {
        root.dataset.pdpCountdownState = "invalid";
        return;
      }
      const state = {
        initialSeconds,
        reset,
        loopSeconds,
        zeroHoldSeconds,
        parts,
        displays,
        startedAt: Date.now(),
        lastRemaining: null,
        tick: 0,
        timer: 0
      };
      countdownStates.set(root, state);
      scheduleCountdown(root);
    });
  }

  async function toggleVideo(root) {
    const video = one("video", root);
    if (!video || root.dataset.pdpAvailable !== SELECTED) return;
    if (video.paused || video.ended) {
      const group = closest(root, "[data-pdp-video-group]");
      if (group) all("[data-pdp-video] video", group).forEach((other) => {
        if (other !== video) { other.pause(); syncVideo(closest(other, "[data-pdp-video]")); }
      });
      hydrateVideoSources(video);
      try { await video.play(); } catch { syncVideo(root); }
    } else video.pause();
  }

  function annotateFallbacks() {
    const explicitGalleries = all("[data-pdp-gallery]");
    explicitGalleries.forEach(annotateGallery);
    all("media-gallery")
      .filter((root) => !root.hasAttribute("data-pdp-gallery")
        && !root.querySelector("[data-pdp-gallery]")
        && !root.closest("[data-pdp-gallery]"))
      .forEach(annotateGallery);
    annotateScrollStrips();
    const disclosureRoots = all("[data-pdp-disclosure], .product__accordion, .faq-item, .store-faq-item, .c-guide, .gp-accordion-item");
    all("details").forEach((details) => {
      if (details.matches("[data-pdp-menu],.menu-drawer-container") || details.closest("[data-pdp-menu-panel], details-modal, .menu-drawer-container")) return;
      if (!disclosureRoots.some((root) => root === details || root.contains(details))) disclosureRoots.push(details);
    });
    disclosureRoots.forEach(annotateDisclosure);
    annotateDialogs();
    annotateOptions();
    annotateBundles();
    annotateDropdowns();
    const explicitCarousels = all("[data-pdp-carousel]");
    explicitCarousels.forEach(annotateCarousel);
    all("gp-carousel-v3, splide-component, slideshow-component, .swiper, swiper-container, .slick-slider")
      .filter((root) => !root.hasAttribute("data-pdp-carousel")
        && !root.querySelector("[data-pdp-carousel]")
        && !root.closest("[data-pdp-carousel]"))
      .forEach(annotateCarousel);
    annotateReviews();
    annotateMenus();
    annotateSearch();
    annotateSticky();
    annotateVideos();
    annotateCountdowns();
  }

  function mediaDimensions(media) {
    const width = Number(media?.getAttribute("width")) || Number(media?.naturalWidth) || Number(media?.videoWidth);
    const height = Number(media?.getAttribute("height")) || Number(media?.naturalHeight) || Number(media?.videoHeight);
    return width > 0 && height > 0 ? { width, height } : null;
  }

  function repairCapturedLayoutRatios() {
    all("[data-pdp-gallery-thumb]").forEach((control) => {
      const item = control.closest("li");
      if (!item) return;
      const current = item.getBoundingClientRect();
      if (current.width <= 1 || current.height > 1) return;
      const dimensions = mediaDimensions(one("img,video", control));
      if (!dimensions) return;
      item.style.height = "auto";
      item.style.aspectRatio = `${dimensions.width} / ${dimensions.height}`;
    });

    all(".banner--adapt").forEach((banner) => {
      if (banner.getBoundingClientRect().height > 1) return;
      const dimensions = mediaDimensions(one("img,video", banner));
      if (!dimensions) return;
      banner.style.height = "auto";
      banner.style.aspectRatio = `${dimensions.width} / ${dimensions.height}`;
    });

    all(".header__heading-link.media.ratio").forEach((link) => {
      const current = link.getBoundingClientRect();
      if (current.width > 1 && current.height > 1) return;
      const image = one("img", link);
      const dimensions = mediaDimensions(image);
      if (!dimensions) return;
      link.style.display = "inline-block";
      link.style.width = `${dimensions.width}px`;
      link.style.height = `${dimensions.height}px`;
      link.style.maxWidth = "100%";
      link.style.aspectRatio = `${dimensions.width} / ${dimensions.height}`;
      image.style.width = "100%";
      image.style.height = "auto";
    });
  }

  document.addEventListener("submit", (event) => {
    if (shopifyMode && (!runtimeRoot.contains(event.target) || event.target.matches('.fast51-product-form'))) return;
    event.preventDefault();
  }, true);
  document.addEventListener("click", (event) => {
    if (shopifyMode && !runtimeRoot.contains(event.target)) return;
    const utilityAction = closest(event.target, "[data-pdp-utility-action]");
    if (utilityAction && activateUtility(utilityAction)) {
      event.preventDefault();
      return;
    }
    const anchor = closest(event.target, "a[href]");
    if (!shopifyMode && (unsafeAnchor(anchor) || inertAction(event.target))) event.preventDefault();

    const activeGuide = closest(event.target, ".c-guide[data-pdp-disclosure]");
    all(".c-guide[data-pdp-disclosure][data-pdp-active='true']")
      .filter((guide) => guide !== activeGuide)
      .forEach((guide) => setDisclosure(guide, false));

    const scrollStrip = closest(event.target, "[data-pdp-scroll-strip]");
    const stripPrevious = closest(event.target, "[data-pdp-scroll-strip-prev]");
    const stripNext = closest(event.target, "[data-pdp-scroll-strip-next]");
    if (scrollStrip && (stripPrevious || stripNext)) {
      event.preventDefault();
      scrollStripStep(scrollStrip, stripNext ? 1 : -1, stripNext || stripPrevious);
      return;
    }

    const galleryRoot = closest(event.target, "[data-pdp-gallery]");
    const thumb = closest(event.target, "[data-pdp-gallery-thumb]");
    if (galleryRoot && thumb) {
      event.preventDefault();
      showGallery(galleryRoot, galleryIndex(galleryRoot, thumb));
      return;
    }
    if (galleryRoot && closest(event.target, "[data-pdp-gallery-prev]")) {
      event.preventDefault();
      showGallery(galleryRoot, integer(galleryRoot.dataset.pdpIndex) - 1);
      return;
    }
    if (galleryRoot && closest(event.target, "[data-pdp-gallery-next]")) {
      event.preventDefault();
      showGallery(galleryRoot, integer(galleryRoot.dataset.pdpIndex) + 1);
      return;
    }

    const disclosureTrigger = closest(event.target, "[data-pdp-disclosure-trigger]");
    if (disclosureTrigger) {
      const root = closest(disclosureTrigger, "[data-pdp-disclosure]");
      if (root) {
        event.preventDefault();
        setDisclosure(root, root.dataset.pdpActive !== SELECTED);
        return;
      }
    }

    const dialogOpener = closest(event.target, "[data-pdp-dialog-open]");
    if (dialogOpener) {
      event.preventDefault();
      openDialog(dialogFor(dialogOpener), dialogOpener);
      return;
    }
    const dialogStateSelector = closest(event.target, "[data-pdp-dialog-state-select]");
    if (dialogStateSelector) {
      const stateDialog = closest(dialogStateSelector, "[data-pdp-dialog]");
      if (stateDialog) {
        event.preventDefault();
        applyDialogState(stateDialog, dialogStateKey(dialogStateSelector));
        return;
      }
    }
    const dialogClose = closest(event.target, "[data-pdp-dialog-close]");
    if (dialogClose) {
      event.preventDefault();
      closeDialog(closest(dialogClose, "[data-pdp-dialog]"));
      return;
    }
    const dialog = closest(event.target, "[data-pdp-dialog]");
    if (dialog && dialog.dataset.pdpActive === SELECTED && (event.target === dialog || closest(event.target, ".modal-overlay") === event.target)) {
      event.preventDefault();
      closeDialog(dialog);
      return;
    }

    const option = closest(event.target, "[data-pdp-option]");
    if (option && !option.matches("input, select, option")) {
      const group = closest(option, "[data-pdp-option-group]");
      const usesActiveClass = all("[data-pdp-option]", group).some((item) => item.classList.contains("is-active"));
      all("[data-pdp-option]", group).forEach((item) => {
        const selected = item === option;
        item.dataset.pdpSelected = String(selected);
        if (item.matches("button, [role='button']")) setBoolean(item, "aria-pressed", selected);
        if (item.matches("[role='tab']") || item.hasAttribute("aria-selected")) {
          setBoolean(item, "aria-selected", selected);
          item.tabIndex = selected ? 0 : -1;
        }
        if (item.matches("[role='radio'], .kaching-bundles__bar") || item.hasAttribute("aria-checked")) {
          setBoolean(item, "aria-checked", selected);
        }
        if (usesActiveClass) item.classList.toggle("is-active", selected);
        if (item.matches(".kaching-bundles__bar")) {
          item.classList.toggle("kaching-bundles__bar--selected", selected);
        }
      });
      group.dataset.pdpValue = option.dataset.value || option.dataset.pdpOption || option.value || "";
      emitChange(group);
      applyOptionSelectionDownstream(group, option);
      return;
    }

    const offer = closest(event.target, "[data-pdp-bundle-offer]");
    const bundleGroup = closest(event.target, "[data-pdp-bundle-group]");
    if (offer && bundleGroup && !closest(event.target, "[data-pdp-dropdown], select, option")) {
      selectBundle(bundleGroup, offer, false);
    }

    const dropdownTrigger = closest(event.target, "[data-pdp-dropdown-trigger]");
    if (dropdownTrigger) {
      event.preventDefault();
      const dropdown = closest(dropdownTrigger, "[data-pdp-dropdown]");
      all("[data-pdp-dropdown][data-pdp-active='true']").filter((item) => item !== dropdown).forEach((item) => setDropdown(item, false));
      setDropdown(dropdown, dropdown.dataset.pdpActive !== SELECTED);
      return;
    }
    const dropdownChoice = closest(event.target, "[data-pdp-dropdown-choice]");
    if (dropdownChoice) {
      event.preventDefault();
      chooseDropdown(closest(dropdownChoice, "[data-pdp-dropdown]"), dropdownChoice);
      return;
    }

    const carousel = closest(event.target, "[data-pdp-carousel]");
    if (carousel && closest(event.target, "[data-pdp-carousel-prev]")) {
      event.preventDefault();
      showCarousel(carousel, integer(carousel.dataset.pdpIndex) - 1);
      scheduleCarouselAutoplay(carousel);
      return;
    }
    if (carousel && closest(event.target, "[data-pdp-carousel-next]")) {
      event.preventDefault();
      showCarousel(carousel, integer(carousel.dataset.pdpIndex) + 1);
      scheduleCarouselAutoplay(carousel);
      return;
    }
    const carouselPage = closest(event.target, "[data-pdp-carousel-page]");
    if (carousel && carouselPage) {
      event.preventDefault();
      showCarousel(carousel, attributeIndex(carouselPage, "data-pdp-carousel-page", 0));
      scheduleCarouselAutoplay(carousel);
      return;
    }

    const reviewControl = closest(event.target, "[data-pdp-review-control]");
    if (reviewControl) {
      event.preventDefault();
      const root = closest(reviewControl, "[data-pdp-review-pages]");
      const command = reviewControl.dataset.pdpReviewControl;
      const current = integer(root.dataset.pdpIndex);
      showReviewPage(root, command === "prev" ? current - 1 : command === "next" ? current + 1 : integer(command, 1) - 1);
      return;
    }

    const menuTrigger = closest(event.target, "[data-pdp-menu-trigger]");
    if (menuTrigger) {
      event.preventDefault();
      const root = closest(menuTrigger, "[data-pdp-menu]");
      setMenu(root, root.dataset.pdpActive !== SELECTED, false);
      return;
    }
    const menuClose = closest(event.target, "[data-pdp-menu-close]");
    if (menuClose) {
      event.preventDefault();
      setMenu(closest(menuClose, "[data-pdp-menu]"), false);
      return;
    }

    const searchTrigger = closest(event.target, "[data-pdp-search-trigger]");
    if (searchTrigger) {
      event.preventDefault();
      const root = closest(searchTrigger, "[data-pdp-search]");
      setSearch(root, root.dataset.pdpActive !== SELECTED, false);
      return;
    }
    const searchClose = closest(event.target, "[data-pdp-search-close]");
    if (searchClose) {
      event.preventDefault();
      setSearch(closest(searchClose, "[data-pdp-search]"), false);
      return;
    }
    const searchClear = closest(event.target, "[data-pdp-search-clear]");
    if (searchClear) {
      event.preventDefault();
      const root = closest(searchClear, "[data-pdp-search]");
      const input = one("[data-pdp-search-input]", root);
      if (input) {
        input.value = "";
        emitChange(input);
        input.focus({ preventScroll: true });
      }
      return;
    }

    const videoToggle = closest(event.target, "[data-pdp-video-toggle], [data-pdp-video-surface-toggle]");
    if (videoToggle) {
      event.preventDefault();
      toggleVideo(closest(videoToggle, "[data-pdp-video]"));
      return;
    }

    if (!closest(event.target, "[data-pdp-dropdown]")) {
      all("[data-pdp-dropdown][data-pdp-active='true']").forEach((dropdown) => setDropdown(dropdown, false));
    }
  });

  document.addEventListener("change", (event) => {
    const group = closest(event.target, "[data-pdp-option-group]");
    if (group) {
      syncOptionGroup(group);
      applyOptionSelectionDownstream(group, selectedOptionControl(group, event.target));
    }
    const selected = event.target;
    if (!(selected instanceof HTMLSelectElement)) return;
    const sticky = closest(selected, "[data-pdp-sticky]");
    const key = selected.name || selected.dataset.variantName || selected.getAttribute("aria-label");
    if (!key) return;
    const peers = all("select").filter((peer) => peer !== selected
      && (peer.name || peer.dataset.variantName || peer.getAttribute("aria-label")) === key
      && (sticky ? !sticky.contains(peer) : closest(peer, "[data-pdp-sticky]")));
    peers.forEach((peer) => {
      if (all("option", peer).some((option) => option.value === selected.value)) peer.value = selected.value;
    });
  });

  document.addEventListener("scroll", (event) => {
    const root = closest(event.target, "[data-pdp-scroll-strip]");
    if (root) syncScrollStrip(root);
  }, true);

  document.addEventListener("touchstart", (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    const strip = closest(event.target, "[data-pdp-scroll-strip]");
    const gallery = strip ? null : closest(event.target, "[data-pdp-gallery]");
    const carousel = strip || gallery ? null : closest(event.target, "[data-pdp-carousel]");
    const owner = strip || gallery || carousel;
    if (!owner) return;
    activeTouchOwner = owner;
    touchStarts.set(owner, {
      x: touch.clientX,
      y: touch.clientY,
      kind: strip ? "strip" : gallery ? "gallery" : "carousel"
    });
  }, { passive: true, capture: true });

  document.addEventListener("touchend", (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;
    const strip = closest(event.target, "[data-pdp-scroll-strip]");
    const gallery = strip ? null : closest(event.target, "[data-pdp-gallery]");
    const carousel = strip || gallery ? null : closest(event.target, "[data-pdp-carousel]");
    const owner = strip || gallery || carousel || activeTouchOwner;
    const start = owner && touchStarts.get(owner);
    if (!owner || !start) return;
    touchStarts.delete(owner);
    activeTouchOwner = null;
    const horizontal = touch.clientX - start.x;
    const vertical = touch.clientY - start.y;
    if (Math.abs(horizontal) < 36 || Math.abs(horizontal) <= Math.abs(vertical)) return;
    const direction = horizontal < 0 ? 1 : -1;
    if (start.kind === "strip") {
      if (owner.dataset.pdpPointerScroll !== SELECTED) scrollStripStep(owner, direction, null);
    }
    else if (start.kind === "gallery") showGallery(owner, integer(owner.dataset.pdpIndex) + direction);
    else {
      showCarousel(owner, integer(owner.dataset.pdpIndex) + direction);
      scheduleCarouselAutoplay(owner);
    }
  }, { passive: true, capture: true });

  document.addEventListener("pointerover", (event) => {
    const root = closest(event.target, "[data-pdp-carousel]");
    if (root && !root.contains(event.relatedTarget)) pauseCarouselAutoplay(root, "pointer");
  });

  document.addEventListener("pointerout", (event) => {
    const root = closest(event.target, "[data-pdp-carousel]");
    if (root && !root.contains(event.relatedTarget)) resumeCarouselAutoplay(root, "pointer");
  });

  document.addEventListener("focusin", (event) => {
    const root = closest(event.target, "[data-pdp-carousel]");
    if (root && !root.contains(event.relatedTarget)) pauseCarouselAutoplay(root, "focus");
  });

  document.addEventListener("focusout", (event) => {
    const root = closest(event.target, "[data-pdp-carousel]");
    if (root && !root.contains(event.relatedTarget)) resumeCarouselAutoplay(root, "focus");
  });

  document.addEventListener("visibilitychange", () => {
    all("[data-pdp-carousel]").forEach((root) => {
      if (document.hidden) pauseCarouselAutoplay(root, "visibility");
      else resumeCarouselAutoplay(root, "visibility");
    });
  });

  reducedMotion.addEventListener("change", () => {
    all("[data-pdp-carousel]").forEach((root) => {
      if (reducedMotion.matches) pauseCarouselAutoplay(root, "motion");
      else resumeCarouselAutoplay(root, "motion");
    });
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      const dialog = one("[data-pdp-dialog][data-pdp-active='true']");
      if (dialog) {
        event.preventDefault();
        closeDialog(dialog);
        return;
      }
      const menu = one("[data-pdp-menu][data-pdp-active='true']");
      if (menu) {
        event.preventDefault();
        setMenu(menu, false);
        return;
      }
      const search = one("[data-pdp-search][data-pdp-active='true']");
      if (search) {
        event.preventDefault();
        setSearch(search, false);
        return;
      }
      const guide = one(".c-guide[data-pdp-disclosure][data-pdp-active='true']");
      if (guide) {
        event.preventDefault();
        setDisclosure(guide, false, true);
        return;
      }
      all("[data-pdp-dropdown][data-pdp-active='true']").forEach((dropdown) => setDropdown(dropdown, false, true));
    }
    if (event.key === "Tab" && activeOverlay) {
      const focusRoot = one("[role='dialog']", activeOverlay) || one("[data-pdp-menu-panel], [data-pdp-search-panel]", activeOverlay) || activeOverlay;
      const focusables = activeFocusables(focusRoot);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  function initialize() {
    setPageScrollLock(false);
    releaseTransientHydrationStyles();
    revealCapturedScrollContent();
    annotateCapturedMarquees();
    annotateImageComparisons();
    revealHydratedReviewWidgets();
    repairCapturedOverflowLists();
    annotateFallbacks();
    repairCapturedLayoutRatios();
    repairScrollTopPlacement();
    html.dataset.pdpInteractiveRuntime = "ready";
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => { html.dataset.pdpFontsReady = "true"; });
    } else html.dataset.pdpFontsReady = "unsupported";
    document.dispatchEvent(new CustomEvent("pdp:runtime-ready"));
  }

  addEventListener("resize", () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      all("[data-pdp-carousel]").forEach((root) => {
        configureCarouselLayout(root);
        showCarousel(root, integer(root.dataset.pdpIndex));
        scheduleCarouselAutoplay(root);
      });
      all("[data-pdp-marquee]").forEach(configureCapturedMarquee);
      repairCapturedOverflowLists();
      repairScrollTopPlacement();
    });
  }, { passive: true });

  addEventListener("load", repairCapturedLayoutRatios, { once: true });

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize, { once: true });
  else initialize();
})();

// Trusted local reference behavior. Uses captured markup/configuration only;
// no storefront scripts, tracking, requests, purchases or persisted user data.
(() => {
  const all = (selector, root = document) => [...root.querySelectorAll(selector)];
  const one = (selector, root = document) => root.querySelector(selector);
  const normalize = value => String(value || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').trim().toLowerCase();
  const json = (selector, root) => { try { return JSON.parse(one(selector, root)?.textContent || '{}'); } catch { return {}; } };

  function initStrip(root) {
    const track=one('[data-pdp-scroll-strip-track]',root);
    if(!track||root.dataset.pdpPointerScroll==='true')return;
    root.dataset.pdpPointerScroll='true';
    track.style.touchAction='pan-x pan-y';track.style.userSelect='none';
    let start=null,suppressUntil=0;
    track.addEventListener('pointerdown',event=>{
      if(event.button!==0||event.pointerType==='touch')return;
      start={id:event.pointerId,x:event.clientX,y:event.clientY,left:track.scrollLeft,drag:false,behavior:track.style.scrollBehavior};
      track.style.scrollBehavior='auto';
    });
    track.addEventListener('pointermove',event=>{
      if(!start||event.pointerId!==start.id)return;
      const dx=event.clientX-start.x,dy=event.clientY-start.y;
      if(!start.drag&&Math.abs(dx)>5&&Math.abs(dx)>Math.abs(dy)){
        start.drag=true;track.setPointerCapture?.(event.pointerId);
      }
      if(!start.drag)return;
      event.preventDefault();track.scrollLeft=start.left-dx;
    });
    const stop=event=>{
      if(!start||event.pointerId!==start.id)return;
      if(start.drag)suppressUntil=performance.now()+300;
      track.style.scrollBehavior=start.behavior;
      start=null;
      if(track.hasPointerCapture?.(event.pointerId))track.releasePointerCapture(event.pointerId);
    };
    track.addEventListener('pointerup',stop);track.addEventListener('pointercancel',stop);
    track.addEventListener('lostpointercapture',stop);
    let touchLeft=0;
    track.addEventListener('touchstart',()=>{touchLeft=track.scrollLeft;},{passive:true});
    track.addEventListener('touchend',()=>{if(Math.abs(track.scrollLeft-touchLeft)>5)suppressUntil=performance.now()+300;},{passive:true});
    track.addEventListener('dragstart',event=>event.preventDefault());
    track.addEventListener('click',event=>{
      // Ignore the physical click released by a drag, while allowing a color
      // or tag change to select its image immediately through thumb.click().
      if(event.isTrusted&&performance.now()<suppressUntil){event.preventDefault();event.stopImmediatePropagation();}
    },true);
  }

  function layoutReviews(host) {
    const grid=one('#grid',host.shadowRoot);
    const cards=grid&&all(':scope > .grid-item-wrap',grid);
    if(!cards?.length||!host.getClientRects().length)return;
    const width=cards[0].getBoundingClientRect().width;
    if(!width)return;
    const columns=Math.max(1,Math.round(grid.clientWidth/width));
    const heights=Array(columns).fill(0);
    for(const card of cards){
      // Provider snapshots can retain an in-flight translation after pagination.
      // The local layout owns final positions; do not add that old offset twice.
      card.style.transform='none';card.style.transition='none';
      const column=heights.indexOf(Math.min(...heights));
      card.style.left=column*width+'px';card.style.top=heights[column]+'px';
      heights[column]+=card.getBoundingClientRect().height;
    }
    grid.style.height=Math.max(...heights)+'px';
  }

  function initSizer(root) {
    const config = json('[data-sizing-config]', root);
    const copy = json('[data-sizing-copy]', root);
    if (!Array.isArray(config.bands) || !config.bands.length) return;
    const weight = one('[data-size-weight]', root);
    const result = one('[data-size-result]', root);
    let unit = root.dataset.defaultUnit === 'kg' ? 'kg' : 'lb';
    let recommended = '';
    let calculated = false;
    const kgValue = () => Math.round(Number(String(weight.value).replace(',', '.')) * (unit === 'lb' ? .45359237 : 1) * 1e9) / 1e9;
    const calculate = () => {
      const kg = kgValue();
      const valid = Number.isFinite(kg) && kg > 0;
      weight.setAttribute('aria-invalid', String(!valid));
      one('[data-size-error]', root).hidden = valid;
      result.hidden = !valid;
      recommended = '';
      if (!valid) return;
      calculated = true;
      const base = config.bands.find(band => kg < band.maxKg) || config.bands.at(-1);
      recommended = base.size;
      const body = one('[data-size-body]:checked', root)?.value;
      const breed = config.breedProfiles?.find(profile => normalize(profile.breed) === normalize(one('[data-size-breed-search]', root)?.value));
      const zone = config.optionalRefinement?.zones?.find(item => kg >= item.minKg && kg <= item.maxKg);
      if (zone && body === 'slim') recommended = zone.smaller;
      else if (zone && body === 'broad') recommended = zone.larger;
      else if (zone && !body && breed?.boundaryBias) recommended = breed.boundaryBias > 0 ? zone.larger : zone.smaller;
      const order = config.bands.map(band => band.size);
      if (Math.abs(order.indexOf(recommended) - order.indexOf(base.size)) > 1) recommended = base.size;
      one('[data-size-result-size]', root).textContent = recommended;
      let explanation = copy.weightOnly;
      if (kg < config.bands[0].minKg) explanation = copy.belowMinimum;
      else if (kg > config.bands.at(-1).maxKg) explanation = copy.aboveMaximum;
      else if (body) explanation = copy[body + 'Result'];
      else if (zone && breed?.boundaryBias) explanation = copy.breedResult;
      one('[data-size-result-copy]', root).textContent = explanation || copy.weightOnly || '';
      const feedback = one('.fourpaw-size-finder__feedback', root);
      if (feedback) {
        feedback.hidden = !(body || breed);
        one('strong', feedback).textContent = (copy.refinedSame || '{size}').replaceAll('{size}', recommended);
        one('p', feedback).textContent = [copy[body], breed?.breed].filter(Boolean).join(' · ');
      }
    };
    const apply = () => {
      if (!recommended) return;
      const section = document.getElementById('shopify-section-' + root.dataset.sectionId) || document;
      const radio = all('.simple-variant-picker input[type="radio"]', section).find(input =>
        (Number(input.dataset.optionPosition) === Number(root.dataset.sizeOptionPosition || 2) || normalize(input.name).includes(normalize(root.dataset.sizeOptionName || 'Size')))
        && normalize(input.value) === normalize(recommended));
      if (!radio || radio.disabled) return;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      root.closest('[data-pdp-dialog]')?.querySelector('[data-pdp-dialog-close]')?.click();
    };
    one('[data-size-calculate]', root)?.addEventListener('click', calculate);
    one('[data-size-apply]', root)?.addEventListener('click', apply);
    one('.fourpaw-size-finder__feedback button', root)?.addEventListener('click', apply);
    weight?.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); calculate(); } });
    weight?.addEventListener('input', () => { if (calculated) calculate(); });
    all('[data-size-unit]', root).forEach(button => button.addEventListener('click', () => {
      const kg = kgValue();
      unit = button.dataset.sizeUnit;
      if (Number.isFinite(kg) && kg > 0) weight.value = String(Math.round((unit === 'lb' ? kg / .45359237 : kg) * 1000) / 1000);
      one('[data-size-unit-label]', root).textContent = unit;
      all('[data-size-unit]', root).forEach(control => control.setAttribute('aria-pressed', String(control.dataset.sizeUnit === unit)));
      if (calculated) calculate();
    }));
    one('[data-size-refine-open]', root)?.addEventListener('click', event => {
      const panel = one('[data-size-refinement]', root);
      panel.hidden = !panel.hidden;
      event.currentTarget.setAttribute('aria-expanded', String(!panel.hidden));
    });
    all('[data-size-body]', root).forEach(input => input.addEventListener('change', calculate));
    one('[data-size-breed-search]', root)?.addEventListener('input', () => { if (calculated) calculate(); });
    one('[data-size-clear]', root)?.addEventListener('click', () => {
      all('[data-size-body]', root).forEach(input => input.checked = false);
      const breed = one('[data-size-breed-search]', root); if (breed) breed.value = '';
      calculate();
    });
  }

  function initTag(preview) {
    const scope = preview.closest('.shop-product-section') || document;
    const name = one('[data-fourpaw-personalization-role="dog_name"] input', scope);
    const phone = one('[data-fourpaw-personalization-role="phone"] input', scope);
    if (!name || !phone) return;
    const panel = preview.closest('[data-pdp-disclosure-panel]');
    const frame = one('.fourpaw-personalization-gallery-frame', scope);
    const thumb = one('.fourpaw-personalization-gallery-thumb', scope);
    const desktop = matchMedia('(min-width:990px)');
    const render = (activate = false) => {
      const n = name.value.trim(), p = phone.value.trim();
      const layout = n && p ? 'both' : n ? 'name-only' : p ? 'phone-only' : 'empty';
      const radio = one('.simple-variant-picker__option.option--color input:checked', scope);
      const color = radio?.dataset.fourpawColorKey || normalize(radio?.value).replace('sky blue', 'blue').replace('gray', 'grey') || 'black';
      const image = preview.dataset['previewImage' + color[0].toUpperCase() + color.slice(1)] || preview.dataset.previewImageBlack;
      for (const target of [preview, frame].filter(Boolean)) {
        target.dataset.layout = layout;
        one('[data-tag-preview-name]', target).textContent = layout === 'empty' ? preview.dataset.namePlaceholder : n;
        one('[data-tag-preview-phone]', target).textContent = layout === 'empty' ? preview.dataset.phonePlaceholder : p;
        const visual = one('[data-tag-preview-visual]', target);
        const template = preview.dataset[layout === 'both' ? 'accessibilityLabelTemplate' : layout === 'name-only' ? 'accessibilityLabelNameTemplate' : layout === 'phone-only' ? 'accessibilityLabelPhoneTemplate' : 'accessibilityLabelEmptyTemplate'];
        visual?.setAttribute('aria-label', String(template || '').replaceAll('[name]', n).replaceAll('[phone]', p));
        const media = one('[data-tag-preview-image]', target);
        if (image && /^(?:(?:\.\/)?(?:assets|preview-assets)\/|https:\/\/cdn\.shopify\.com\/)/.test(image)) { media.removeAttribute('srcset'); media.src = image; }
      }
      preview.dataset.previewColor = color;
      for (const input of [name, phone]) {
        const counter = document.getElementById('current-' + input.id);
        if (counter) counter.textContent = String(input.value.length);
      }
      const open = panel && !panel.hidden;
      preview.hidden = !open;
      all('[data-fourpaw-personalization-role]', scope).forEach(field => field.hidden = !open);
      preview.toggleAttribute('data-desktop-gallery-source', desktop.matches && Boolean(frame));
      if (thumb) {
        thumb.hidden = !open || !desktop.matches;
        thumb.classList.toggle('visually-hidden', thumb.hidden);
        thumb.setAttribute('aria-hidden', String(thumb.hidden));
        const media = one('img', thumb); if (image && media) { media.removeAttribute('srcset'); media.src = image; }
      }
      if (frame && open && desktop.matches) {
        frame.hidden = false;
        if (activate) thumb?.click();
      }
      requestAnimationFrame(() => all('[data-tag-preview-name],[data-tag-preview-phone]', scope).forEach(output => {
        output.style.removeProperty('font-size');
        if (output.clientWidth && output.scrollWidth > output.clientWidth) output.style.fontSize = Math.max(6, parseFloat(getComputedStyle(output).fontSize) * output.clientWidth / output.scrollWidth * .98) + 'px';
      }));
    };
    for (const field of [name, phone]) {
      field.addEventListener('input', () => render(true));
      field.addEventListener('focus', () => render(true));
    }
    scope.addEventListener('change', () => render(Boolean(panel && !panel.hidden)));
    if (panel) new MutationObserver(() => render(!panel.hidden)).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    desktop.addEventListener('change', () => render());
    render();
  }

  function initReviews(host) {
    const root = host.shadowRoot;
    if (!root) return;
    const pages = all('template[data-pdp-review-page]', root);
    let index = 0;
    let scheduled=false;
    const schedule=()=>{
      if(scheduled)return;scheduled=true;
      requestAnimationFrame(()=>{scheduled=false;layoutReviews(host);});
    };
    new ResizeObserver(schedule).observe(host);
    root.addEventListener('load',schedule,true);
    document.fonts?.ready.then(schedule);
    schedule();
    root.addEventListener('click', event => {
      const button = event.target.closest('#loadMore,[data-testid="load-more-button"]');
      if (!button || index >= pages.length) return;
      event.preventDefault();
      const previousBody = one('.skill-review-body', root);
      const replacement = pages[index++].content.querySelector('.skill-review-body')?.cloneNode(true);
      if (!replacement) return;
      previousBody.replaceWith(replacement);
      host.dataset.pdpReviewPage = String(index);
      const more = one('#loadMore,[data-testid="load-more-button"]', replacement);
      // Provider CSS may force display even on [hidden] controls. Remove the
      // exhausted button so it cannot remain visible with nothing left to load.
      if (index === pages.length && more) more.remove();
      schedule();
    });
  }

  function init() {
    const scope = document.querySelector('script[data-pdp-runtime-mode="shopify"]') ? document.querySelector('main#MainContent') : document;
    if (!scope) return;
    all('[data-pdp-scroll-strip]', scope).forEach(initStrip);
    all('video[loop][muted]', scope).forEach(video=>{
      if(video.dataset.pdpAutoplayReady)return;
      video.dataset.pdpAutoplayReady='true';video.autoplay=true;video.muted=true;video.playsInline=true;video.controls=false;
      const host=video.closest('[data-autoplay-blocked]')||video.parentElement;
      host?.removeAttribute('data-autoplay-blocked');host?.querySelectorAll('.custom-benefit-video-play,[data-video-play]').forEach(button=>button.remove());
      const play=()=>video.play().catch(()=>{});play();video.addEventListener('loadeddata',play);
      new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting))play();}).observe(video);
    });
    all('fourpaw-size-finder', scope).forEach(initSizer);
    all('[data-fourpaw-tag-preview]', scope).forEach(initTag);
    all('[data-skill-reviews] > div', scope).forEach(initReviews);
    document.addEventListener('change', event => {
      const input = event.target;
      if (!scope.contains(input) || !input.matches('.simple-variant-picker input[type="radio"]')) return;
      const option = input.closest('.simple-variant-picker__option');
      const label = one('.simple-variant-picker__selected-value', option || document);
      if (label) label.textContent = input.value;
    });
    all('[data-fourpaw-scroll-to-size]', scope).forEach(button => button.addEventListener('click', () => {
      const target = one('.simple-variant-picker__option.option--size', scope) || one('[data-size-chart-popup]', scope);
      target?.scrollIntoView({ block: 'center', behavior: 'instant' });
      const trigger = one('[data-size-chart-popup]', scope);
      trigger?.focus({ preventScroll: true });
    }));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
